import Constant from '@/common/constant/Constant';
import TelephoneConstant from '@/common/constant/TelephoneConstant';
import AddFieldTag from '@/component/formManagement/addField/AddFieldTag';
import AddFieldDate from '@/component/formManagement/addField/AddFieldDate';
import AddFieldNumber from '@/component/formManagement/addField/AddFieldNumber';
import AddFieldText from '@/component/formManagement/addField/AddFieldText';
import AddFieldRichText from '@/component/formManagement/addField/AddFieldRichText';
import AddFieldIframe from '@/component/formManagement/addField/AddFieldIframe';
import AddFieldStatus from '@/component/formManagement/addField//AddFieldStatus';
import AddFieldURL from '@/component/formManagement/addField//AddFieldURL';
import Utils from '@/common/utils/utils';
import { cloneDeep } from 'lodash';
import { sortListByDictionary } from './FormListHandler';
import { validateFieldInput } from './FieldListHandler';
import AddFieldMermaid from '@/component/formManagement/addField/AddFieldMermaid';
import AddFieldHtml from '@/component/formManagement/addField/AddFieldHtml';
import AddFieldMarkdown from '@/component/formManagement/addField/AddFieldMarkdown';
import AddFieldJson from '@/component/formManagement/addField/AddFieldJson';
import { calculateIndexClosePanel } from '@/component/multiPanel/panel/handler/PanelHandler';
import { TELEPHONE_FIELD_ORDER } from '@/common/constant/SystemField';
import OpenAiUtil from '@/common/utils/OpenAiUtil';
import { trI18n } from '@/common/utils/trI18n';

const BUSINESS_APP_FIELD_ORDER = [
    Constant.SYSTEM_FIELD.BUSINESS_APP_DISPLAY_SIDE_BAR,
    Constant.SYSTEM_FIELD.BUSINESS_APP_USE_RANGE,
    Constant.SYSTEM_FIELD.MCP_SERVER_SETTING,
];
/**
 * Is permission add item tag of create ticket
 * @param {*} argsCommon
 * @returns
 */
export const isPermissionAddItemTagOfCreateTicket = (argsCommon) => {
    let tagField = argsCommon.tagField;
    if (
        !tagField.structure.isAddItem ||
        Utils.isEmptyString(tagField.structure.userPermissionCreateItemTag)
        || (tagField.structure.settingSelectItem === Constant.SETTING_SELECT_ITEM_TAG_FIELD.MASTER_KNOWLEDGE)
    ) {
        argsCommon.setIsPermissionAddItemTag(false);
        return;
    }

    // check when isAddItem is true
    let isProjectManager;
    switch (tagField.structure.userPermissionCreateItemTag) {
        // PROJECT_MANAGEMENT
        case Constant.USER_PERMISSION_CREATE_ITEM_TAG.PROJECT_MANAGEMENT:
            isProjectManager = argsCommon.isProjectManager;
            argsCommon.setIsPermissionAddItemTag(isProjectManager);
            break;
        // PROJECT_MEMEBER
        case Constant.USER_PERMISSION_CREATE_ITEM_TAG.PROJECT_MEMEBER:
            let isExistMember =
                Utils.findIndexInArrayById(
                    argsCommon.userDetail.id,
                    argsCommon.userListOfProject
                ) !== -1
                    ? true
                    : false;
            argsCommon.setIsPermissionAddItemTag(isExistMember);
            break;
        // ALL_USER_IN_ORG
        case Constant.USER_PERMISSION_CREATE_ITEM_TAG.ALL_USER_IN_ORG:
            let isPermission =
                argsCommon.userDetail.affiliationOrganization === argsCommon.corporationDetail.id;
            argsCommon.setIsPermissionAddItemTag(isPermission);
            break;
        // ALL_USER
        case Constant.USER_PERMISSION_CREATE_ITEM_TAG.ALL_USER:
            argsCommon.setIsPermissionAddItemTag(true);
            break;
        default:
            argsCommon.setIsPermissionAddItemTag(true);
            break;
    }
};

/**
 * Check isAddNewField
 * @param {*} field
 * @returns isAddNewField
 */
export const isAddNewField = (field) => {
    if (Utils.isEmpty(field)) {
        return false;
    }
    return [
        Constant.CLIENT_FIELD_TYPE.ADD_NEW_FIELD,
        Constant.CLIENT_FIELD_TYPE.DUPLICATE_FIELD,
    ].includes(field.clientFieldType);
};

export const sortFieldListByCreator = (fieldList, creator) => {
    // sort field list by createAt
    // let sortFieldList = Utils.sortByCreatedAtDesc(fieldList);
    let sortFieldList = Utils.sortByCreatedAtAsc(fieldList);

    // field list by userId
    let fieldListByUserId = sortFieldList.filter((field) => field.creator === creator);
    // field list by not userId
    let fieldListByNotUserId = sortFieldList.filter((field) => field.creator !== creator);
    sortFieldList = fieldListByUserId.concat(fieldListByNotUserId);

    return sortFieldList;
};

/**
 * Get the order value for a field in a specific project
 * @param {Object} field - Field object
 * @param {string} projectId - Project ID
 * @returns {number} Order value or NO_ORDER_VALUE if not set
 */
const getFieldOrderForProject = (field, projectId) => {
    return field.order && typeof field.order[projectId] === 'number'
        ? field.order[projectId]
        : Constant.NO_ORDER_VALUE;
};

/**
 * Check if a field is a telephone field
 * @param {Object} field - Field object
 * @returns {boolean} True if field is a telephone field
 */
const isTelephoneField = (field) => {
    return TELEPHONE_FIELD_ORDER.indexOf(field.sysField) !== -1;
};

/**
 * Compare two fields by their explicit order values
 * @param {number} orderA - Order value of field A
 * @param {number} orderB - Order value of field B
 * @returns {number|null} Comparison result or null if not both have order
 */
const compareByOrder = (orderA, orderB) => {
    if (orderA !== Constant.NO_ORDER_VALUE && orderB !== Constant.NO_ORDER_VALUE) {
        return orderA - orderB;
    }
    return null;
};

/**
 * Compare special business app fields (DISPLAY_SIDE_BAR vs USE_RANGE)
 * @param {Object} fieldA - First field object
 * @param {Object} fieldB - Second field object
 * @returns {number|null} Comparison result or null if not special fields
 */
const compareSpecialBusinessFields = (fieldA, fieldB) => {
    const indexA = BUSINESS_APP_FIELD_ORDER.indexOf(fieldA.sysField);
    const indexB = BUSINESS_APP_FIELD_ORDER.indexOf(fieldB.sysField);

    if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
    }
    return null;
};

/**
 * Compare telephone fields, ensuring they are grouped together at the end
 * @param {Object} fieldA - First field object
 * @param {Object} fieldB - Second field object
 * @returns {number|null} Comparison result or null if neither is telephone field
 */
const compareTelephoneFields = (fieldA, fieldB) => {
    const isATelephone = isTelephoneField(fieldA);
    const isBTelephone = isTelephoneField(fieldB);

    // Both are telephone fields: sort by defined order
    if (isATelephone && isBTelephone) {
        return TELEPHONE_FIELD_ORDER.indexOf(fieldA.sysField) -
               TELEPHONE_FIELD_ORDER.indexOf(fieldB.sysField);
    }

    // 分析機能 (CR-297430) is always the last field of the list: a telephone field without order stays before it
    // Only A is telephone: push to end (but before 分析機能)
    if (isATelephone) return isKibanaAnalysisField(fieldB) ? -1 : 1;

    // Only B is telephone: push to end (but before 分析機能)
    if (isBTelephone) return isKibanaAnalysisField(fieldA) ? 1 : -1;

    return null;
};

const isKibanaAnalysisField = field => field?.sysField === Constant.SYSTEM_FIELD.KIBANA_ANALYSIS;

/**
 * Compare two fields by their creation date
 * @param {Object} fieldA - First field object
 * @param {Object} fieldB - Second field object
 * @returns {number} Comparison result (negative if A is older)
 */
const compareByCreatedAt = (fieldA, fieldB) => {
    const dateA = fieldA.createdAt ? new Date(fieldA.createdAt).getTime() : 0;
    const dateB = fieldB.createdAt ? new Date(fieldB.createdAt).getTime() : 0;
    return dateA - dateB;
};

/**
 * Main comparator function for sorting fields with multiple priority levels
 * @param {Object} fieldA - First field object
 * @param {Object} fieldB - Second field object
 * @param {string} projectId - Current project ID
 * @returns {number} Comparison result for Array.sort()
 */
const compareFields = (fieldA, fieldB, projectId) => {
    const orderA = getFieldOrderForProject(fieldA, projectId);
    const orderB = getFieldOrderForProject(fieldB, projectId);

    // Priority 1: Compare by explicit order
    const orderResult = compareByOrder(orderA, orderB);
    if (orderResult !== null) {
        return orderResult;
    }

    // Priority 2: Special business app fields
    const businessResult = compareSpecialBusinessFields(fieldA, fieldB);
    if (businessResult !== null) {
        return businessResult;
    }

    // Priority 3: Telephone fields grouping
    const telephoneResult = compareTelephoneFields(fieldA, fieldB);
    if (telephoneResult !== null) {
        return telephoneResult;
    }

    // Priority 4: Fallback to createdAt
    return compareByCreatedAt(fieldA, fieldB);
};

/**
 * Sort field list by order for a specific project
 * Returns a new sorted array without mutating the original
 * @param {Array} fieldList - Array of field objects to sort
 * @param {Object} projectInuse - Current project object with id
 * @returns {Array} New sorted field list
 */
export const sortFieldListByOrder = (fieldList, projectInuse) => {
    const projectId = projectInuse.id;
    const fieldListClone = [...fieldList];

    return fieldListClone.sort((a, b) => compareFields(a, b, projectId));
};


/**
 * Sort field list by dictionary
 * @param {*} fieldList
 * @returns fieldList
 */
export const sortFieldListByDictionary = (fieldList) => {
    return fieldList.sort((a, b) => {
        let nameA = Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, a.name).toUpperCase();
        let nameB = Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, b.name).toUpperCase();
        return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
    });
};

/**
 * generateItemFieldStatus
 * @param {*} item
 * @returns itemData
 */
export const generateItemFieldStatus = (item) => {
    let itemField = {
        id: item.id,
        name: typeof item.name === 'string' ? JSON.parse(item.name) : item.name,
        viewForCustomer:
            typeof item.viewForCustomer === 'string'
                ? JSON.parse(item.viewForCustomer)
                : item.viewForCustomer,
        structure: typeof item.structure === 'string' ? JSON.parse(item.structure) : item.structure,
        isShowInfo: false,
        isErrorValidate: false,
        errorMsg: {},
    };

    return itemField;
};

/**
 * generateItemFieldTag
 * @param {*} item
 * @returns itemData
 */
export const generateItemFieldTag = (item) => {
    let name = '';
    try {
        name = JSON.parse(item.name);
        name = Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, item.name);
    } catch (err) {
        name = item.name;
    }

    let structure;
    try {
        structure = JSON.parse(item.structure);
    } catch (err) {
        structure = item.structure;
    }
    let itemField = {
        id: item.id,
        name: name,
        isCheckbox: structure.isShowItemInTicket,
        isErrorValidate: false,
        isEditName: false,
        no: structure.no,
        hitNumber: Utils.isEmpty(item.hitNumber) ? 0 : item.hitNumber
    };

    return itemField;
};

/**
 * Get add field by fieldType
 * @param {*} argsCommon
 * @returns uiAddField
 */
export const getAddFieldByFieldType = (argsCommon, fieldType, disableBtnShowMore) => {
    let guiAddField;
    let currentAddField = { ...fieldType };
    if (!Utils.isEmpty(currentAddField.name)) {
        currentAddField.defaultValue = Utils.getTextByLanguage(
            Constant.LANGUAGE_TYPE.JP,
            fieldType.name
        );
    }
    switch (fieldType.type) {
        // tag
        case Constant.FORM_TYPE.FIELD_TAG:
        case Constant.FIELD_UI.TAG:
            guiAddField = (
                <AddFieldTag
                    key={`add_field_tag_${fieldType.id}`}
                    API={argsCommon.API}
                    corporationDetail={argsCommon.corporationDetail}
                    addFormType={argsCommon.addFormType}
                    userDetail={argsCommon.userDetail}
                    projectDetail={argsCommon.projectDetail}
                    systemFieldList={argsCommon.systemFieldList}
                    projectList={argsCommon.projectList}
                    formList={argsCommon.formList}
                    setFieldTypeSelected={argsCommon.setFieldTypeSelected}
                    fieldList={argsCommon.fieldList}
                    setFieldList={argsCommon.setFieldList}
                    fieldAddList={argsCommon.fieldAddList}
                    setFieldAddList={argsCommon.setFieldAddList}
                    currentAddField={fieldType}
                    fieldTypeSelectedList={argsCommon.fieldTypeSelectedList}
                    setFieldTypeSelectedList={argsCommon.setFieldTypeSelectedList}
                    isUsingProjectCheckbox={argsCommon.isUsingProjectCheckbox}
                    isIndexNumber={argsCommon.isIndexNumber}
                    panelScreen={argsCommon.panelScreen}
                    setPanelScreen={argsCommon.setPanelScreen}
                    currentUpdateField={argsCommon.currentUpdateField}
                    setCurrentUpdateField={argsCommon.setCurrentUpdateField}
                    parentPanelScreen={argsCommon.parentPanelScreen}
                    setParentPanelScreen={argsCommon.setParentPanelScreen}
                    regionDropIdBlock={argsCommon.regionDropIdBlock}
                    setRegionDropIdBlock={argsCommon.setRegionDropIdBlock}
                    listFieldAddToForm={argsCommon.listFieldAddToForm}
                    setListFieldAddToForm={argsCommon.setListFieldAddToForm}
                    listFieldInProject={argsCommon.listFieldInProject}
                    setListFieldInProject={argsCommon.setListFieldInProject}
                    disableBtnShowMore={disableBtnShowMore}
                    fieldLisForUpdate={argsCommon.fieldLisForUpdate}
                    setFieldLisForUpdate={argsCommon.setFieldLisForUpdate}
                    fieldListInState={argsCommon.fieldListInState}
                    fieldDragDropList={argsCommon.fieldDragDropList}

                    handleClick={argsCommon.handleClick}
                    handleClose={argsCommon.handleClose}
                    setCurrentFieldSelected={argsCommon.setCurrentFieldSelected}
                    setIsOpenConfirmDeleteModal={argsCommon.setIsOpenConfirmDeleteModal}
                    settingTitle = {argsCommon.settingTitle}
                    fieldRefList={argsCommon.fieldRefList}
                    setFieldRefList={argsCommon.setFieldRefList}

                    setIsChangeData={argsCommon.setIsChangeData}
                    isChangeData={argsCommon.isChangeData}
                    setIsEditedData={argsCommon.setIsEditedData}

                    allListCorp={argsCommon.allListCorp}
                    b2bCustomerList={argsCommon.b2bCustomerList}
                    b2bHelpDeskList={argsCommon.b2bHelpDeskList}

                    formName={argsCommon.formName}

                />
            );
            break;
        // text
        case Constant.FORM_TYPE.FIELD_TEXT:
        case Constant.FIELD_UI.TEXT:
            guiAddField = (
                <AddFieldText
                    key={`add_field_text_${fieldType.id}`}
                    API={argsCommon.API}
                    corporationDetail={argsCommon.corporationDetail}
                    addFormType={argsCommon.addFormType}
                    userDetail={argsCommon.userDetail}
                    projectDetail={argsCommon.projectDetail}
                    systemFieldList={argsCommon.systemFieldList}
                    projectList={argsCommon.projectList}
                    formList={argsCommon.formList}
                    setFieldTypeSelected={argsCommon.setFieldTypeSelected}
                    fieldList={argsCommon.fieldList}
                    settingTitle = {argsCommon.settingTitle}
                    setFieldList={argsCommon.setFieldList}
                    fieldAddList={argsCommon.fieldAddList}
                    setFieldAddList={argsCommon.setFieldAddList}
                    currentAddField={fieldType}
                    fieldTypeSelectedList={argsCommon.fieldTypeSelectedList}
                    setFieldTypeSelectedList={argsCommon.setFieldTypeSelectedList}
                    isUsingProjectCheckbox={argsCommon.isUsingProjectCheckbox}
                    isIndexNumber={argsCommon.isIndexNumber}
                    regionDropIdBlock={argsCommon.regionDropIdBlock}
                    setRegionDropIdBlock={argsCommon.setRegionDropIdBlock}
                    listFieldAddToForm={argsCommon.listFieldAddToForm}
                    setListFieldAddToForm={argsCommon.setListFieldAddToForm}
                    listFieldInProject={argsCommon.listFieldInProject}
                    setListFieldInProject={argsCommon.setListFieldInProject}
                    currentUpdateField={argsCommon.currentUpdateField}
                    setCurrentUpdateField={argsCommon.setCurrentUpdateField}
                    parentPanelScreen={argsCommon.parentPanelScreen}
                    setParentPanelScreen={argsCommon.setParentPanelScreen}
                    disableBtnShowMore={disableBtnShowMore}
                    fieldLisForUpdate={argsCommon.fieldLisForUpdate}
                    setFieldLisForUpdate={argsCommon.setFieldLisForUpdate}
                    fieldListInState={argsCommon.fieldListInState}
                    fieldDragDropList={argsCommon.fieldDragDropList}

                    handleClick={argsCommon.handleClick}
                    handleClose={argsCommon.handleClose}
                    setCurrentFieldSelected={argsCommon.setCurrentFieldSelected}
                    setIsOpenConfirmDeleteModal={argsCommon.setIsOpenConfirmDeleteModal}

                    fieldRefList={argsCommon.fieldRefList}
                    setFieldRefList={argsCommon.setFieldRefList}

                    setIsChangeData={argsCommon.setIsChangeData}
                    isChangeData={argsCommon.isChangeData}

                    allListCorp={argsCommon.allListCorp}
                    b2bCustomerList={argsCommon.b2bCustomerList}
                    b2bHelpDeskList={argsCommon.b2bHelpDeskList}
                    formName={argsCommon.formName}
                />
            );
            break;
        // number
        case Constant.FORM_TYPE.FIELD_INT:
        case Constant.FIELD_UI.NUMBER:
            guiAddField = (
                <AddFieldNumber
                    key={`add_field_number_${fieldType.id}`}
                    API={argsCommon.API}
                    corporationDetail={argsCommon.corporationDetail}
                    addFormType={argsCommon.addFormType}
                    userDetail={argsCommon.userDetail}
                    projectDetail={argsCommon.projectDetail}
                    systemFieldList={argsCommon.systemFieldList}
                    projectList={argsCommon.projectList}
                    formList={argsCommon.formList}
                    settingTitle = {argsCommon.settingTitle}
                    setFieldTypeSelected={argsCommon.setFieldTypeSelected}
                    fieldList={argsCommon.fieldList}
                    setFieldList={argsCommon.setFieldList}
                    fieldAddList={argsCommon.fieldAddList}
                    setFieldAddList={argsCommon.setFieldAddList}
                    currentAddField={fieldType}
                    fieldTypeSelectedList={argsCommon.fieldTypeSelectedList}
                    setFieldTypeSelectedList={argsCommon.setFieldTypeSelectedList}
                    isUsingProjectCheckbox={argsCommon.isUsingProjectCheckbox}
                    isIndexNumber={argsCommon.isIndexNumber}
                    regionDropIdBlock={argsCommon.regionDropIdBlock}
                    setRegionDropIdBlock={argsCommon.setRegionDropIdBlock}
                    listFieldAddToForm={argsCommon.listFieldAddToForm}
                    setListFieldAddToForm={argsCommon.setListFieldAddToForm}
                    listFieldInProject={argsCommon.listFieldInProject}
                    setListFieldInProject={argsCommon.setListFieldInProject}
                    currentUpdateField={argsCommon.currentUpdateField}
                    setCurrentUpdateField={argsCommon.setCurrentUpdateField}
                    parentPanelScreen={argsCommon.parentPanelScreen}
                    setParentPanelScreen={argsCommon.setParentPanelScreen}
                    disableBtnShowMore={disableBtnShowMore}
                    fieldLisForUpdate={argsCommon.fieldLisForUpdate}
                    setFieldLisForUpdate={argsCommon.setFieldLisForUpdate}
                    fieldListInState={argsCommon.fieldListInState}
                    fieldDragDropList={argsCommon.fieldDragDropList}

                    handleClick={argsCommon.handleClick}
                    handleClose={argsCommon.handleClose}
                    setCurrentFieldSelected={argsCommon.setCurrentFieldSelected}
                    setIsOpenConfirmDeleteModal={argsCommon.setIsOpenConfirmDeleteModal}

                    fieldRefList={argsCommon.fieldRefList}
                    setFieldRefList={argsCommon.setFieldRefList}

                    setIsChangeData={argsCommon.setIsChangeData}
                    isChangeData={argsCommon.isChangeData}

                    allListCorp={argsCommon.allListCorp}
                    b2bCustomerList={argsCommon.b2bCustomerList}
                    b2bHelpDeskList={argsCommon.b2bHelpDeskList}
                    formName={argsCommon.formName}
                />
            );
            break;
        // date
        case Constant.FORM_TYPE.FIELD_DATE:
        case Constant.FIELD_UI.DATE:
            guiAddField = (
                <AddFieldDate
                    corporationDetail={argsCommon.corporationDetail}
                    key={`add_field_date_${fieldType.id}`}
                    API={argsCommon.API}
                    settingTitle = {argsCommon.settingTitle}
                    addFormType={argsCommon.addFormType}
                    userDetail={argsCommon.userDetail}
                    projectDetail={argsCommon.projectDetail}
                    systemFieldList={argsCommon.systemFieldList}
                    projectList={argsCommon.projectList}
                    formList={argsCommon.formList}
                    setFieldTypeSelected={argsCommon.setFieldTypeSelected}
                    fieldList={argsCommon.fieldList}
                    setFieldList={argsCommon.setFieldList}
                    fieldAddList={argsCommon.fieldAddList}
                    setFieldAddList={argsCommon.setFieldAddList}
                    currentAddField={fieldType}
                    fieldTypeSelectedList={argsCommon.fieldTypeSelectedList}
                    setFieldTypeSelectedList={argsCommon.setFieldTypeSelectedList}
                    isUsingProjectCheckbox={argsCommon.isUsingProjectCheckbox}
                    isIndexNumber={argsCommon.isIndexNumber}
                    regionDropIdBlock={argsCommon.regionDropIdBlock}
                    setRegionDropIdBlock={argsCommon.setRegionDropIdBlock}
                    listFieldAddToForm={argsCommon.listFieldAddToForm}
                    setListFieldAddToForm={argsCommon.setListFieldAddToForm}
                    listFieldInProject={argsCommon.listFieldInProject}
                    setListFieldInProject={argsCommon.setListFieldInProject}
                    currentUpdateField={argsCommon.currentUpdateField}
                    setCurrentUpdateField={argsCommon.setCurrentUpdateField}
                    parentPanelScreen={argsCommon.parentPanelScreen}
                    setParentPanelScreen={argsCommon.setParentPanelScreen}
                    disableBtnShowMore={disableBtnShowMore}
                    fieldLisForUpdate={argsCommon.fieldLisForUpdate}
                    setFieldLisForUpdate={argsCommon.setFieldLisForUpdate}
                    fieldListInState={argsCommon.fieldListInState}
                    fieldDragDropList={argsCommon.fieldDragDropList}

                    handleClick={argsCommon.handleClick}
                    handleClose={argsCommon.handleClose}
                    setCurrentFieldSelected={argsCommon.setCurrentFieldSelected}
                    setIsOpenConfirmDeleteModal={argsCommon.setIsOpenConfirmDeleteModal}

                    fieldRefList={argsCommon.fieldRefList}
                    setFieldRefList={argsCommon.setFieldRefList}

                    setIsChangeData={argsCommon.setIsChangeData}
                    isChangeData={argsCommon.isChangeData}

                    allListCorp={argsCommon.allListCorp}
                    b2bCustomerList={argsCommon.b2bCustomerList}
                    b2bHelpDeskList={argsCommon.b2bHelpDeskList}
                    formName={argsCommon.formName}
                />
            );
            break;

        // status
        case Constant.FIELD_UI.WORKFLOW:
        case Constant.FORM_TYPE.FIELD_WORKFLOW:
            guiAddField = (
                <AddFieldStatus
                    corporationDetail={argsCommon.corporationDetail}
                    key={`add_field_workflow_${fieldType.id}`}
                    API={argsCommon.API}
                    settingTitle = {argsCommon.settingTitle}
                    addFormType={argsCommon.addFormType}
                    userDetail={argsCommon.userDetail}
                    projectDetail={argsCommon.projectDetail}
                    systemFieldList={argsCommon.systemFieldList}
                    projectList={argsCommon.projectList}
                    formList={argsCommon.formList}
                    setFieldTypeSelected={argsCommon.setFieldTypeSelected}
                    fieldList={argsCommon.fieldList}
                    setFieldList={argsCommon.setFieldList}
                    fieldAddList={argsCommon.fieldAddList}
                    setFieldAddList={argsCommon.setFieldAddList}
                    currentAddField={fieldType}
                    fieldTypeSelectedList={argsCommon.fieldTypeSelectedList}
                    setFieldTypeSelectedList={argsCommon.setFieldTypeSelectedList}
                    isUsingProjectCheckbox={argsCommon.isUsingProjectCheckbox}
                    isIndexNumber={argsCommon.isIndexNumber}
                    panelScreen={argsCommon.panelScreen}
                    setPanelScreen={argsCommon.setPanelScreen}
                    currentUpdateField={argsCommon.currentUpdateField}
                    setCurrentUpdateField={argsCommon.setCurrentUpdateField}
                    parentPanelScreen={argsCommon.parentPanelScreen}
                    setParentPanelScreen={argsCommon.setParentPanelScreen}
                    regionDropIdBlock={argsCommon.regionDropIdBlock}
                    setRegionDropIdBlock={argsCommon.setRegionDropIdBlock}
                    listFieldAddToForm={argsCommon.listFieldAddToForm}
                    setListFieldAddToForm={argsCommon.setListFieldAddToForm}
                    listFieldInProject={argsCommon.listFieldInProject}
                    setListFieldInProject={argsCommon.setListFieldInProject}
                    disableBtnShowMore={disableBtnShowMore}
                    fieldLisForUpdate={argsCommon.fieldLisForUpdate}
                    setFieldLisForUpdate={argsCommon.setFieldLisForUpdate}
                    fieldListInState={argsCommon.fieldListInState}
                    fieldDragDropList={argsCommon.fieldDragDropList}

                    handleClick={argsCommon.handleClick}
                    handleClose={argsCommon.handleClose}
                    setCurrentFieldSelected={argsCommon.setCurrentFieldSelected}
                    setIsOpenConfirmDeleteModal={argsCommon.setIsOpenConfirmDeleteModal}

                    fieldRefList={argsCommon.fieldRefList}
                    setFieldRefList={argsCommon.setFieldRefList}
                    allListCorp={argsCommon.allListCorp}
                    setIsChangeData={argsCommon.setIsChangeData}
                    isChangeData={argsCommon.isChangeData}
                    formName={argsCommon.formName}
                    getAddFieldByFieldType
                    isAllowCustomerChangeStatus={argsCommon.isAllowCustomerChangeStatus}
                    setIsAllowCustomerChangeStatus={argsCommon.setIsAllowCustomerChangeStatus}

                />
            );
            break;

        // iframe
        case Constant.FIELD_UI.IFRAME:
        case Constant.FORM_TYPE.FIELD_IFRAME:
            guiAddField = (
                <AddFieldIframe
                    key={`add_field_iframe_${fieldType.id}`}
                    API={argsCommon.API}
                    corporationDetail={argsCommon.corporationDetail}
                    settingTitle = {argsCommon.settingTitle}
                    addFormType={argsCommon.addFormType}
                    userDetail={argsCommon.userDetail}
                    projectDetail={argsCommon.projectDetail}
                    systemFieldList={argsCommon.systemFieldList}
                    projectList={argsCommon.projectList}
                    formList={argsCommon.formList}
                    setFieldTypeSelected={argsCommon.setFieldTypeSelected}
                    fieldList={argsCommon.fieldList}
                    setFieldList={argsCommon.setFieldList}
                    fieldAddList={argsCommon.fieldAddList}
                    setFieldAddList={argsCommon.setFieldAddList}
                    currentAddField={fieldType}
                    fieldTypeSelectedList={argsCommon.fieldTypeSelectedList}
                    setFieldTypeSelectedList={argsCommon.setFieldTypeSelectedList}
                    isUsingProjectCheckbox={argsCommon.isUsingProjectCheckbox}
                    isIndexNumber={argsCommon.isIndexNumber}
                    panelScreen={argsCommon.panelScreen}
                    setPanelScreen={argsCommon.setPanelScreen}
                    currentUpdateField={argsCommon.currentUpdateField}
                    setCurrentUpdateField={argsCommon.setCurrentUpdateField}
                    parentPanelScreen={argsCommon.parentPanelScreen}
                    setParentPanelScreen={argsCommon.setParentPanelScreen}
                    regionDropIdBlock={argsCommon.regionDropIdBlock}
                    setRegionDropIdBlock={argsCommon.setRegionDropIdBlock}
                    listFieldAddToForm={argsCommon.listFieldAddToForm}
                    setListFieldAddToForm={argsCommon.setListFieldAddToForm}
                    listFieldInProject={argsCommon.listFieldInProject}
                    setListFieldInProject={argsCommon.setListFieldInProject}
                    disableBtnShowMore={disableBtnShowMore}
                    fieldLisForUpdate={argsCommon.fieldLisForUpdate}
                    setFieldLisForUpdate={argsCommon.setFieldLisForUpdate}
                    fieldListInState={argsCommon.fieldListInState}
                    fieldDragDropList={argsCommon.fieldDragDropList}

                    handleClick={argsCommon.handleClick}
                    handleClose={argsCommon.handleClose}
                    setCurrentFieldSelected={argsCommon.setCurrentFieldSelected}
                    setIsOpenConfirmDeleteModal={argsCommon.setIsOpenConfirmDeleteModal}

                    fieldRefList={argsCommon.fieldRefList}
                    setFieldRefList={argsCommon.setFieldRefList}

                    setIsChangeData={argsCommon.setIsChangeData}
                    isChangeData={argsCommon.isChangeData}

                    allListCorp={argsCommon.allListCorp}
                    b2bCustomerList={argsCommon.b2bCustomerList}
                    b2bHelpDeskList={argsCommon.b2bHelpDeskList}
                    formName={argsCommon.formName}

                />
            );
            break;

        // URL
        case Constant.FIELD_UI.URL:
        case Constant.FORM_TYPE.FIELD_URL:
            guiAddField = (
                <AddFieldURL
                    key={`add_field_url_${fieldType.id}`}
                    API={argsCommon.API}
                    corporationDetail={argsCommon.corporationDetail}
                    settingTitle = {argsCommon.settingTitle}
                    addFormType={argsCommon.addFormType}
                    userDetail={argsCommon.userDetail}
                    projectDetail={argsCommon.projectDetail}
                    systemFieldList={argsCommon.systemFieldList}
                    projectList={argsCommon.projectList}
                    formList={argsCommon.formList}
                    setFieldTypeSelected={argsCommon.setFieldTypeSelected}
                    fieldList={argsCommon.fieldList}
                    setFieldList={argsCommon.setFieldList}
                    fieldAddList={argsCommon.fieldAddList}
                    setFieldAddList={argsCommon.setFieldAddList}
                    currentAddField={fieldType}
                    fieldTypeSelectedList={argsCommon.fieldTypeSelectedList}
                    setFieldTypeSelectedList={argsCommon.setFieldTypeSelectedList}
                    isUsingProjectCheckbox={argsCommon.isUsingProjectCheckbox}
                    isIndexNumber={argsCommon.isIndexNumber}
                    panelScreen={argsCommon.panelScreen}
                    setPanelScreen={argsCommon.setPanelScreen}
                    currentUpdateField={argsCommon.currentUpdateField}
                    setCurrentUpdateField={argsCommon.setCurrentUpdateField}
                    parentPanelScreen={argsCommon.parentPanelScreen}
                    setParentPanelScreen={argsCommon.setParentPanelScreen}
                    regionDropIdBlock={argsCommon.regionDropIdBlock}
                    setRegionDropIdBlock={argsCommon.setRegionDropIdBlock}
                    listFieldAddToForm={argsCommon.listFieldAddToForm}
                    setListFieldAddToForm={argsCommon.setListFieldAddToForm}
                    listFieldInProject={argsCommon.listFieldInProject}
                    setListFieldInProject={argsCommon.setListFieldInProject}
                    disableBtnShowMore={disableBtnShowMore}
                    fieldLisForUpdate={argsCommon.fieldLisForUpdate}
                    setFieldLisForUpdate={argsCommon.setFieldLisForUpdate}
                    fieldListInState={argsCommon.fieldListInState}
                    fieldDragDropList={argsCommon.fieldDragDropList}

                    handleClick={argsCommon.handleClick}
                    handleClose={argsCommon.handleClose}
                    setCurrentFieldSelected={argsCommon.setCurrentFieldSelected}
                    setIsOpenConfirmDeleteModal={argsCommon.setIsOpenConfirmDeleteModal}

                    fieldRefList={argsCommon.fieldRefList}
                    setFieldRefList={argsCommon.setFieldRefList}

                    setIsChangeData={argsCommon.setIsChangeData}
                    isChangeData={argsCommon.isChangeData}

                    allListCorp={argsCommon.allListCorp}
                    b2bCustomerList={argsCommon.b2bCustomerList}
                    b2bHelpDeskList={argsCommon.b2bHelpDeskList}
                    formName={argsCommon.formName}

                />
            );
            break;
        case Constant.FORM_TYPE.FIELD_RICHTEXT:
        case Constant.FIELD_UI.FIELD_RICHTEXT:
            guiAddField = (
                <AddFieldRichText
                    corporationDetail={argsCommon.corporationDetail}
                    key={`add_field_text_${fieldType.id}`}
                    API={argsCommon.API}
                    settingTitle = {argsCommon.settingTitle}
                    addFormType={argsCommon.addFormType}
                    userDetail={argsCommon.userDetail}
                    projectDetail={argsCommon.projectDetail}
                    systemFieldList={argsCommon.systemFieldList}
                    projectList={argsCommon.projectList}
                    formList={argsCommon.formList}
                    setFieldTypeSelected={argsCommon.setFieldTypeSelected}
                    fieldList={argsCommon.fieldList}
                    setFieldList={argsCommon.setFieldList}
                    fieldAddList={argsCommon.fieldAddList}
                    setFieldAddList={argsCommon.setFieldAddList}
                    currentAddField={fieldType}
                    fieldTypeSelectedList={argsCommon.fieldTypeSelectedList}
                    setFieldTypeSelectedList={argsCommon.setFieldTypeSelectedList}
                    isUsingProjectCheckbox={argsCommon.isUsingProjectCheckbox}
                    isIndexNumber={argsCommon.isIndexNumber}
                    regionDropIdBlock={argsCommon.regionDropIdBlock}
                    setRegionDropIdBlock={argsCommon.setRegionDropIdBlock}
                    listFieldAddToForm={argsCommon.listFieldAddToForm}
                    setListFieldAddToForm={argsCommon.setListFieldAddToForm}
                    listFieldInProject={argsCommon.listFieldInProject}
                    setListFieldInProject={argsCommon.setListFieldInProject}
                    currentUpdateField={argsCommon.currentUpdateField}
                    setCurrentUpdateField={argsCommon.setCurrentUpdateField}
                    parentPanelScreen={argsCommon.parentPanelScreen}
                    setParentPanelScreen={argsCommon.setParentPanelScreen}
                    disableBtnShowMore={disableBtnShowMore}
                    fieldLisForUpdate={argsCommon.fieldLisForUpdate}
                    setFieldLisForUpdate={argsCommon.setFieldLisForUpdate}
                    fieldListInState={argsCommon.fieldListInState}
                    fieldDragDropList={argsCommon.fieldDragDropList}

                    handleClick={argsCommon.handleClick}
                    handleClose={argsCommon.handleClose}
                    setCurrentFieldSelected={argsCommon.setCurrentFieldSelected}
                    setIsOpenConfirmDeleteModal={argsCommon.setIsOpenConfirmDeleteModal}

                    fieldRefList={argsCommon.fieldRefList}
                    setFieldRefList={argsCommon.setFieldRefList}

                    setIsChangeData={argsCommon.setIsChangeData}
                    isChangeData={argsCommon.isChangeData}

                    allListCorp={argsCommon.allListCorp}
                    b2bCustomerList={argsCommon.b2bCustomerList}
                    b2bHelpDeskList={argsCommon.b2bHelpDeskList}
                    formName={argsCommon.formName}

                />
            );
            break;
        case Constant.FORM_TYPE.MERMAID:
        case Constant.FIELD_UI.MERMAID:
            guiAddField = (
                <AddFieldMermaid
                    corporationDetail={argsCommon.corporationDetail}
                    key={`add_field_text_${fieldType.id}`}
                    API={argsCommon.API}
                    settingTitle = {argsCommon.settingTitle}
                    addFormType={argsCommon.addFormType}
                    userDetail={argsCommon.userDetail}
                    projectDetail={argsCommon.projectDetail}
                    systemFieldList={argsCommon.systemFieldList}
                    projectList={argsCommon.projectList}
                    formList={argsCommon.formList}
                    setFieldTypeSelected={argsCommon.setFieldTypeSelected}
                    fieldList={argsCommon.fieldList}
                    setFieldList={argsCommon.setFieldList}
                    fieldAddList={argsCommon.fieldAddList}
                    setFieldAddList={argsCommon.setFieldAddList}
                    currentAddField={fieldType}
                    fieldTypeSelectedList={argsCommon.fieldTypeSelectedList}
                    setFieldTypeSelectedList={argsCommon.setFieldTypeSelectedList}
                    isUsingProjectCheckbox={argsCommon.isUsingProjectCheckbox}
                    isIndexNumber={argsCommon.isIndexNumber}
                    regionDropIdBlock={argsCommon.regionDropIdBlock}
                    setRegionDropIdBlock={argsCommon.setRegionDropIdBlock}
                    listFieldAddToForm={argsCommon.listFieldAddToForm}
                    setListFieldAddToForm={argsCommon.setListFieldAddToForm}
                    listFieldInProject={argsCommon.listFieldInProject}
                    setListFieldInProject={argsCommon.setListFieldInProject}
                    currentUpdateField={argsCommon.currentUpdateField}
                    setCurrentUpdateField={argsCommon.setCurrentUpdateField}
                    parentPanelScreen={argsCommon.parentPanelScreen}
                    setParentPanelScreen={argsCommon.setParentPanelScreen}
                    disableBtnShowMore={disableBtnShowMore}
                    fieldLisForUpdate={argsCommon.fieldLisForUpdate}
                    setFieldLisForUpdate={argsCommon.setFieldLisForUpdate}
                    fieldListInState={argsCommon.fieldListInState}
                    fieldDragDropList={argsCommon.fieldDragDropList}

                    handleClick={argsCommon.handleClick}
                    handleClose={argsCommon.handleClose}
                    setCurrentFieldSelected={argsCommon.setCurrentFieldSelected}
                    setIsOpenConfirmDeleteModal={argsCommon.setIsOpenConfirmDeleteModal}

                    fieldRefList={argsCommon.fieldRefList}
                    setFieldRefList={argsCommon.setFieldRefList}

                    setIsChangeData={argsCommon.setIsChangeData}
                    isChangeData={argsCommon.isChangeData}

                    allListCorp={argsCommon.allListCorp}
                    b2bCustomerList={argsCommon.b2bCustomerList}
                    b2bHelpDeskList={argsCommon.b2bHelpDeskList}
                    formName={argsCommon.formName}
                />
            );
            break;
        case Constant.FORM_TYPE.HTML:
        case Constant.FIELD_UI.HTML:
            guiAddField = (
                <AddFieldHtml
                    corporationDetail={argsCommon.corporationDetail}
                    key={`add_field_text_${fieldType.id}`}
                    API={argsCommon.API}
                    settingTitle = {argsCommon.settingTitle}
                    addFormType={argsCommon.addFormType}
                    userDetail={argsCommon.userDetail}
                    projectDetail={argsCommon.projectDetail}
                    systemFieldList={argsCommon.systemFieldList}
                    projectList={argsCommon.projectList}
                    formList={argsCommon.formList}
                    setFieldTypeSelected={argsCommon.setFieldTypeSelected}
                    fieldList={argsCommon.fieldList}
                    setFieldList={argsCommon.setFieldList}
                    fieldAddList={argsCommon.fieldAddList}
                    setFieldAddList={argsCommon.setFieldAddList}
                    currentAddField={fieldType}
                    fieldTypeSelectedList={argsCommon.fieldTypeSelectedList}
                    setFieldTypeSelectedList={argsCommon.setFieldTypeSelectedList}
                    isUsingProjectCheckbox={argsCommon.isUsingProjectCheckbox}
                    isIndexNumber={argsCommon.isIndexNumber}
                    regionDropIdBlock={argsCommon.regionDropIdBlock}
                    setRegionDropIdBlock={argsCommon.setRegionDropIdBlock}
                    listFieldAddToForm={argsCommon.listFieldAddToForm}
                    setListFieldAddToForm={argsCommon.setListFieldAddToForm}
                    listFieldInProject={argsCommon.listFieldInProject}
                    setListFieldInProject={argsCommon.setListFieldInProject}
                    currentUpdateField={argsCommon.currentUpdateField}
                    setCurrentUpdateField={argsCommon.setCurrentUpdateField}
                    parentPanelScreen={argsCommon.parentPanelScreen}
                    setParentPanelScreen={argsCommon.setParentPanelScreen}
                    disableBtnShowMore={disableBtnShowMore}
                    fieldLisForUpdate={argsCommon.fieldLisForUpdate}
                    setFieldLisForUpdate={argsCommon.setFieldLisForUpdate}
                    fieldListInState={argsCommon.fieldListInState}
                    fieldDragDropList={argsCommon.fieldDragDropList}
                    handleClick={argsCommon.handleClick}
                    handleClose={argsCommon.handleClose}
                    setCurrentFieldSelected={argsCommon.setCurrentFieldSelected}
                    setIsOpenConfirmDeleteModal={argsCommon.setIsOpenConfirmDeleteModal}
                    fieldRefList={argsCommon.fieldRefList}
                    setFieldRefList={argsCommon.setFieldRefList}
                    setIsChangeData={argsCommon.setIsChangeData}
                    isChangeData={argsCommon.isChangeData}
                    allListCorp={argsCommon.allListCorp}
                    b2bCustomerList={argsCommon.b2bCustomerList}
                    b2bHelpDeskList={argsCommon.b2bHelpDeskList}
                    formName={argsCommon.formName}
                />
            );
            break;
        case Constant.FORM_TYPE.MARKDOWN:
        case Constant.FIELD_UI.MARKDOWN:
            guiAddField = (
                <AddFieldMarkdown
                    corporationDetail={argsCommon.corporationDetail}
                    key={`add_field_text_${fieldType.id}`}
                    API={argsCommon.API}
                    settingTitle={argsCommon.settingTitle}
                    addFormType={argsCommon.addFormType}
                    userDetail={argsCommon.userDetail}
                    projectDetail={argsCommon.projectDetail}
                    systemFieldList={argsCommon.systemFieldList}
                    projectList={argsCommon.projectList}
                    formList={argsCommon.formList}
                    setFieldTypeSelected={argsCommon.setFieldTypeSelected}
                    fieldList={argsCommon.fieldList}
                    setFieldList={argsCommon.setFieldList}
                    fieldAddList={argsCommon.fieldAddList}
                    setFieldAddList={argsCommon.setFieldAddList}
                    currentAddField={fieldType}
                    fieldTypeSelectedList={argsCommon.fieldTypeSelectedList}
                    setFieldTypeSelectedList={argsCommon.setFieldTypeSelectedList}
                    isUsingProjectCheckbox={argsCommon.isUsingProjectCheckbox}
                    isIndexNumber={argsCommon.isIndexNumber}
                    regionDropIdBlock={argsCommon.regionDropIdBlock}
                    setRegionDropIdBlock={argsCommon.setRegionDropIdBlock}
                    listFieldAddToForm={argsCommon.listFieldAddToForm}
                    setListFieldAddToForm={argsCommon.setListFieldAddToForm}
                    listFieldInProject={argsCommon.listFieldInProject}
                    setListFieldInProject={argsCommon.setListFieldInProject}
                    currentUpdateField={argsCommon.currentUpdateField}
                    setCurrentUpdateField={argsCommon.setCurrentUpdateField}
                    parentPanelScreen={argsCommon.parentPanelScreen}
                    setParentPanelScreen={argsCommon.setParentPanelScreen}
                    disableBtnShowMore={disableBtnShowMore}
                    fieldLisForUpdate={argsCommon.fieldLisForUpdate}
                    setFieldLisForUpdate={argsCommon.setFieldLisForUpdate}
                    fieldListInState={argsCommon.fieldListInState}
                    fieldDragDropList={argsCommon.fieldDragDropList}
                    handleClick={argsCommon.handleClick}
                    handleClose={argsCommon.handleClose}
                    setCurrentFieldSelected={argsCommon.setCurrentFieldSelected}
                    setIsOpenConfirmDeleteModal={argsCommon.setIsOpenConfirmDeleteModal}
                    fieldRefList={argsCommon.fieldRefList}
                    setFieldRefList={argsCommon.setFieldRefList}
                    setIsChangeData={argsCommon.setIsChangeData}
                    isChangeData={argsCommon.isChangeData}
                    allListCorp={argsCommon.allListCorp}
                    b2bCustomerList={argsCommon.b2bCustomerList}
                    b2bHelpDeskList={argsCommon.b2bHelpDeskList}
                    formName={argsCommon.formName}
                />
            );
            break;
        case Constant.FORM_TYPE.JSON:
        case Constant.FIELD_UI.JSON:
            guiAddField = (
                <AddFieldJson
                    corporationDetail={argsCommon.corporationDetail}
                    key={`add_field_text_${fieldType.id}`}
                    API={argsCommon.API}
                    settingTitle={argsCommon.settingTitle}
                    addFormType={argsCommon.addFormType}
                    userDetail={argsCommon.userDetail}
                    projectDetail={argsCommon.projectDetail}
                    systemFieldList={argsCommon.systemFieldList}
                    projectList={argsCommon.projectList}
                    formList={argsCommon.formList}
                    setFieldTypeSelected={argsCommon.setFieldTypeSelected}
                    fieldList={argsCommon.fieldList}
                    setFieldList={argsCommon.setFieldList}
                    fieldAddList={argsCommon.fieldAddList}
                    setFieldAddList={argsCommon.setFieldAddList}
                    currentAddField={fieldType}
                    fieldTypeSelectedList={argsCommon.fieldTypeSelectedList}
                    setFieldTypeSelectedList={argsCommon.setFieldTypeSelectedList}
                    isUsingProjectCheckbox={argsCommon.isUsingProjectCheckbox}
                    isIndexNumber={argsCommon.isIndexNumber}
                    regionDropIdBlock={argsCommon.regionDropIdBlock}
                    setRegionDropIdBlock={argsCommon.setRegionDropIdBlock}
                    listFieldAddToForm={argsCommon.listFieldAddToForm}
                    setListFieldAddToForm={argsCommon.setListFieldAddToForm}
                    listFieldInProject={argsCommon.listFieldInProject}
                    setListFieldInProject={argsCommon.setListFieldInProject}
                    currentUpdateField={argsCommon.currentUpdateField}
                    setCurrentUpdateField={argsCommon.setCurrentUpdateField}
                    parentPanelScreen={argsCommon.parentPanelScreen}
                    setParentPanelScreen={argsCommon.setParentPanelScreen}
                    disableBtnShowMore={disableBtnShowMore}
                    fieldLisForUpdate={argsCommon.fieldLisForUpdate}
                    setFieldLisForUpdate={argsCommon.setFieldLisForUpdate}
                    fieldListInState={argsCommon.fieldListInState}
                    fieldDragDropList={argsCommon.fieldDragDropList}
                    handleClick={argsCommon.handleClick}
                    handleClose={argsCommon.handleClose}
                    setCurrentFieldSelected={argsCommon.setCurrentFieldSelected}
                    setIsOpenConfirmDeleteModal={argsCommon.setIsOpenConfirmDeleteModal}
                    fieldRefList={argsCommon.fieldRefList}
                    setFieldRefList={argsCommon.setFieldRefList}
                    setIsChangeData={argsCommon.setIsChangeData}
                    isChangeData={argsCommon.isChangeData}
                    allListCorp={argsCommon.allListCorp}
                    b2bCustomerList={argsCommon.b2bCustomerList}
                    b2bHelpDeskList={argsCommon.b2bHelpDeskList}
                    formName={argsCommon.formName}
                />
            );
            break;

        default:
            guiAddField = null;
    }
    return guiAddField;
};

/**
 * Add field new
 * @param {*} argsCommon
 */
export const addFieldNew = (fieldType, argsCommon, fieldInputData) => {
    let fieldData = {
        id: Utils.isEmpty(fieldInputData?.id) ? `dummy_${Date.now().toString()}` : fieldInputData?.id,
        usage: Constant.FORM_USAGE.FIELD,
        name: {
            jp: {
                A: 'F',
                text: '',
            },
        },
        affiliationOrganization: argsCommon.corporationDetail.id,
        affiliationProject: argsCommon.projectDetail.id,
        affiliationField: '',
        creator: argsCommon.userDetail.id,
        updator: argsCommon.userDetail.id,
        viewForCustomer: JSON.stringify({}),
        definition: Constant.FORM_DEFINITION.USER,
        explanation: {},
        usingProjects:
            Utils.isEmpty(fieldInputData) || Utils.isEmptyArray(fieldInputData.usingProjects)
                ? []
                : fieldInputData.usingProjects,
        isUsingProject: false,
        isUsingForm: true,
        clientFieldType: Constant.CLIENT_FIELD_TYPE.ADD_NEW_FIELD,
        isShowInfo: true,
        addToForm: true,
        order: Utils.isEmpty(fieldInputData) ? JSON.stringify({}) : fieldInputData.order,
        saveOrder: Utils.isEmpty(fieldInputData) ? JSON.stringify({}) : fieldInputData.saveOrder,
        relatedMasterForm: Constant.RELATED_MASTER_FORM_DEFAULT,
        errors: {}
    };

    switch (fieldType) {
        // tag
        case Constant.FIELD_UI.TAG:
            fieldData.type = Constant.FORM_TYPE.FIELD_TAG;
            fieldData.structure = {
                UI: Constant.FIELD_UI.TAG,
                isMultiple: Constant.TAG_FIELD_DEFAULT.IS_MULTIPLE,
                optionType: Constant.TAG_FIELD_DEFAULT.CHECK_OPTION_TYPE,
                recordNumber: Constant.TAG_FIELD_DEFAULT.RECORD_NUMBER,
                isAddItem: Constant.TAG_FIELD_DEFAULT.IS_ADD_ITEM,
                settingSelectItem: Constant.SETTING_SELECT_ITEM_TAG_FIELD.DEFAULT,
                displayOrder: Constant.DISPLAY_ORDER_TAG_FIELD.DEFAULT,
                elasticSearch: '',
                userPermissionCreateItemTag:
                    Constant.USER_PERMISSION_CREATE_ITEM_TAG.PROJECT_MANAGEMENT,
                isAutoAddData: Constant.MASTER_FIELD_AUTO_ADD_DATA.ON,
                isAutoFill: Constant.MASTER_FIELD_AUTO_FILL.ON,
                offAutoLink: [],
                isSaveItemList: false,
            };
            fieldData.itemList = [];
            fieldData.defaultItemList = [];
            break;
        // text
        case Constant.FIELD_UI.TEXT:
            fieldData.type = Constant.FORM_TYPE.FIELD_TEXT;
            fieldData.structure = {
                UI: Constant.FIELD_UI.TEXT,
                minLength: Constant.TEXT_FIELD_DEFAULT.MIN_LENGTH,
                maxLength: Constant.TEXT_FIELD_DEFAULT.MAX_LENGTH,
                inputFormat: Constant.TEXT_FIELD_DEFAULT.INPUT_FORMAT,
                elasticSearch: '',
            };
            break;
        case Constant.FIELD_UI.FIELD_RICHTEXT:
            fieldData.type = Constant.FORM_TYPE.FIELD_RICHTEXT;
            fieldData.structure = {
                UI: Constant.FIELD_UI.FIELD_RICHTEXT,
                minLength: Constant.TEXT_FIELD_DEFAULT.MIN_LENGTH,
                maxLength: Constant.RICHTEXT_MAX_LENGTH,
                inputFormat: Constant.TEXT_FIELD_DEFAULT.INPUT_FORMAT,
                elasticSearch: '',
            };
            break;
        // number
        case Constant.FIELD_UI.NUMBER:
            fieldData.type = Constant.FORM_TYPE.FIELD_INT;
            fieldData.structure = {
                UI: Constant.FIELD_UI.NUMBER,
                elasticSearch: '',
                unit: Constant.NUMBER_FIELD_DEFAULT.UNIT,
                unitPosition: Constant.NUMBER_FIELD_DEFAULT.UNIT_POSITION_AFTER,
                minValue: Constant.NUMBER_FIELD_DEFAULT.MIN_VALUE,
                maxValue: Constant.NUMBER_FIELD_DEFAULT.MAX_VALUE,
                integerOnly: Constant.NUMBER_FIELD_DEFAULT.INTEGER_ONLY,
            };
            break;
        // date
        case Constant.FIELD_UI.DATE:
            fieldData.type = Constant.FORM_TYPE.FIELD_DATE;
            fieldData.structure = {
                UI: Constant.FIELD_UI.DATE,
                dateFormat: Constant.SETTING_DATE_FIELD_FORMAT.YYYYMMDD_HHMM,
                elasticSearch: '',
            };
            break;
        // WORKFLOW
        case Constant.FIELD_UI.WORKFLOW:
            fieldData.type = Constant.FORM_TYPE.FIELD_WORKFLOW;
            fieldData.structure = {
                UI: Constant.FIELD_UI.SELECTOR,
                workFlow: false,
                elasticSearch: '',
            };
            fieldData.itemList = [];
            fieldData.defaultItemList = [];
            break;
        // Iframe
        case Constant.FIELD_UI.IFRAME:
            fieldData.type = Constant.FORM_TYPE.FIELD_IFRAME;
            fieldData.structure = {
                UI: Constant.FIELD_UI.IFRAME,
                elasticSearch: '',
            };
            break;
        // URL
        case Constant.FIELD_UI.URL:
            fieldData.type = Constant.FORM_TYPE.FIELD_URL;
            fieldData.structure = {
                UI: Constant.FIELD_UI.URL,
                elasticSearch: '',
            };
            break;
        case Constant.FIELD_UI.MERMAID:
            fieldData.type = Constant.FORM_TYPE.MERMAID;
            fieldData.structure = {
                UI: Constant.FIELD_UI.MERMAID,
                minLength: Constant.TEXT_FIELD_DEFAULT.MIN_LENGTH,
                maxLength: Constant.RICHTEXT_MAX_LENGTH,
                inputFormat: Constant.TEXT_FIELD_DEFAULT.INPUT_FORMAT,
                elasticSearch: '',
            };
            break;
        case Constant.FIELD_UI.HTML:
            fieldData.type = Constant.FORM_TYPE.HTML;
            fieldData.structure = {
                UI: Constant.FIELD_UI.HTML,
                minLength: Constant.TEXT_FIELD_DEFAULT.MIN_LENGTH,
                maxLength: Constant.RICHTEXT_MAX_LENGTH,
                inputFormat: Constant.TEXT_FIELD_DEFAULT.INPUT_FORMAT,
                elasticSearch: '',
            };
            break;
        case Constant.FIELD_UI.MARKDOWN:
            fieldData.type = Constant.FORM_TYPE.MARKDOWN;
            fieldData.structure = {
                UI: Constant.FIELD_UI.MARKDOWN,
                minLength: Constant.TEXT_FIELD_DEFAULT.MIN_LENGTH,
                inputFormat: Constant.TEXT_FIELD_DEFAULT.INPUT_FORMAT,
                elasticSearch: '',
            };
            break;
        case Constant.FIELD_UI.JSON:
            fieldData.type = Constant.FORM_TYPE.JSON;
            fieldData.structure = {
                UI: Constant.FIELD_UI.JSON,
                minLength: Constant.TEXT_FIELD_DEFAULT.MIN_LENGTH,
                inputFormat: Constant.TEXT_FIELD_DEFAULT.INPUT_FORMAT,
                elasticSearch: '',
            };
            break;
        default:
            fieldData = null;
    }

    // push field to field add list
    let fieldAddList = [...argsCommon.fieldAddList];
    fieldAddList.push(fieldData);
    argsCommon.setFieldAddList(fieldAddList);

    let cloneFieldTypeSelectedList = cloneDeep(argsCommon.fieldTypeSelectedList);
    let fieldTypeSelected = {
        id: fieldData.id,
        type: fieldType,
        defaultValue: '',
        ...fieldData,
    };
    cloneFieldTypeSelectedList = cloneFieldTypeSelectedList.concat([fieldTypeSelected]);
    argsCommon.setFieldTypeSelectedList(cloneFieldTypeSelectedList);
};

/**
 * Build create item tag request
 * @param {*} tagField
 * @param {*} itemList
 * @returns itemList
 */
export const buildCreateItemTagRequest = (tagField, itemList) => {
    if (Utils.isEmptyArray(itemList)) {
        return [];
    }

    let requestItemList = [];

    itemList.map(async (item) => {
        let requestTagItem = {
            usage: Constant.FORM_USAGE.TAG,
            name: JSON.stringify({
                jp: {
                    A: 'F',
                    text: item.name,
                },
            }),
            affiliationOrganization: tagField.affiliationOrganization,
            affiliationProject: tagField.affiliationProject,
            affiliationField: tagField.id,
            creator: tagField.creator,
            updator: tagField.updator,
            type: '',
            structure: JSON.stringify({
                isShowItemInTicket: item.isCheckbox,
                no: item.no,
            }),
            viewForCustomer: JSON.stringify({}),
            definition: Constant.FORM_DEFINITION.USER,
            explanation: JSON.stringify({}),
            usingProjects: tagField.usingProjects,
            hitNumber: 0
        };

        // push to array
        requestItemList.push(requestTagItem);
    });

    return requestItemList;
};

/**
 * Build create item status request
 * @param {*} statusField
 * @param {*} itemList
 * @returns itemList
 */
export const buildCreateItemStatusRequest = (statusField, itemList) => {
    if (Utils.isEmptyArray(itemList)) {
        return [];
    }

    let requestItemList = [];

    itemList.forEach(item => {
        if (Utils.findIndexInArrayById(item.id, statusField.defaultItemList) === -1 || statusField.clientFieldType === Constant.CLIENT_FIELD_TYPE.DUPLICATE_FIELD) {
            let statusItem = {
                usage: Constant.FORM_USAGE.STATUS,
                name: JSON.stringify(item.name),
                affiliationOrganization: statusField.affiliationOrganization,
                affiliationProject: statusField.affiliationProject,
                affiliationField: statusField.id,
                creator: statusField.creator,
                updator: statusField.updator,
                type: '',
                structure: JSON.stringify(item.structure),
                viewForCustomer: JSON.stringify(item.viewForCustomer),
                definition: Constant.FORM_DEFINITION.USER,
                explanation: JSON.stringify({}),
                usingProjects: []
            };

            // push to array
            requestItemList.push(statusItem);
        }
    });

    return requestItemList;
};

/**
 * Build create field request
 * @param {*} fieldList
 * @param {*} projectDetail
 * @return fieldList
 */
export const buildCreateFieldRequest = (fieldList, projectDetail) => {
    let requestFieldList = [];

    fieldList.map(async (field) => {
        let requestField = { ...field };
        //  edit field
        if (isAddNewField(field)) {
            delete requestField.id;
            delete requestField.createdAt;

            // set usingProjects
            let usingProjects = field.usingProjects;
            if (field.clientFieldType === Constant.CLIENT_FIELD_TYPE.DUPLICATE_FIELD) {
                usingProjects = [projectDetail.id];
            }

            if (requestField.type === Constant.FORM_TYPE.FIELD_INT) {
                let structure = requestField.structure;
                structure.unit = (structure.unit)?.trim();
                requestField.structure = structure;
            }

            let order = Utils.isTypeObject(requestField.saveOrder) ? JSON.stringify(requestField.saveOrder) : requestField.saveOrder;

            // add field
            // convert inputData to requestData
            delete requestField.isUsingProject;
            delete requestField.isErrorValidate;
            delete requestField.isProjectInuse;
            delete requestField.isShowInfo;
            delete requestField.clientFieldType;
            delete requestField.isErrorMinLargerMaxLength;
            delete requestField.itemList;
            delete requestField.defaultItemList;
            delete requestField.addToForm;
            delete requestField.errors;
            delete requestField.isUsingForm;
            delete requestField.updatedAt;
            delete requestField.orderObject;
            delete requestField.saveOrder;
            delete requestField.hideInProject;
            delete requestField.structure.isSaveItemList;

            requestField.order = order;
            requestField.usingProjects = usingProjects;
            requestField.name = Utils.isTypeObject(requestField.name) ? JSON.stringify(requestField.name) : requestField.name;
            requestField.structure = Utils.isTypeObject(requestField.structure) ? JSON.stringify(requestField.structure) : requestField.structure;
            requestField.explanation = Utils.isTypeObject(requestField.explanation) ? JSON.stringify(requestField.explanation) : requestField.explanation;
            requestField.viewForCustomer = Utils.isTypeObject(requestField.viewForCustomer) ?
                JSON.stringify(requestField.viewForCustomer) : requestField.viewForCustomer;

            let itemList = [];
            // create tag field item
            if (field.structure.UI === Constant.FIELD_UI.TAG && field.structure.settingSelectItem !== Constant.SETTING_SELECT_ITEM_TAG_FIELD.MASTER_KNOWLEDGE && field.structure.settingSelectItem !== Constant.SETTING_SELECT_ITEM_TAG_FIELD.AUTO_FILL) {
                itemList = buildCreateItemTagRequest(field, field.itemList);
            }

            // create status field item
            if (field.type === Constant.FORM_TYPE.FIELD_WORKFLOW) {
                itemList = buildCreateItemStatusRequest(field, field.itemList);
            }

            switch (field.structure.settingSelectItem) {
                // master knowledge
                case Constant.SETTING_SELECT_ITEM_TAG_FIELD.MASTER_KNOWLEDGE:
                    let masterKnowledge = field.masterKnowledge;
                    let structure = JSON.parse(requestField.structure);

                    // structure.formMaster = masterKnowledge.formMaster;
                    // structure.isAutoAddData = masterKnowledge.isAutoAddData;
                    requestField.relatedMasterForm = masterKnowledge.formMaster;
                    requestField.settingAutoAdd = masterKnowledge.isAutoAddData;

                    structure.listItem = masterKnowledge.listItem;

                    delete requestField.masterKnowledge;
                    requestField.structure = JSON.stringify(structure);

                    requestField.definition = Constant.FORM_DEFINITION.SYSTEM_MASTER_FIELD;
                    break;
                case Constant.SETTING_SELECT_ITEM_TAG_FIELD.AUTO_FILL:
                    let autoFillMasterField = field.autoFillMasterField;
                    let autoFillStructure = JSON.parse(requestField.structure);

                    // structure.formMaster = autoFillMasterField.formMaster;
                    // structure.isAutoAddData = autoFillMasterField.isAutoAddData;
                    requestField.relatedMasterForm = autoFillMasterField.formMaster;
                    requestField.settingAutoAdd = autoFillMasterField.isAutoAddData;
                    autoFillStructure.listItem = autoFillMasterField.listItem;
                    delete requestField.autoFillMasterField;
                    requestField.structure = JSON.stringify(autoFillStructure);
                    requestField.definition = Constant.FORM_DEFINITION.SYSTEM_MASTER_FIELD;
                    break;
                default:
                    break;
            }
            requestField.shareOption = requestField?.newShareOption ? requestField?.newShareOption : 1;
            requestField.shareRange = requestField?.newShareRange;
            delete requestField?.newShareOption;
            delete requestField?.newShareRange
            
            // push to array
            requestFieldList.push({
                field: requestField,
                itemList: itemList
            });
        }
    });

    return requestFieldList;
};


export const buildEditFieldRequest = (fieldList, projectDetail, parentPanelScreen) => {
    let requestFieldList = [];
    let addNewItemList = [];

    fieldList.map(async field => {
        let requestField = cloneDeep(field);
        let defaultFieldData = cloneDeep(field);

        let editItemData;

        // FIELD_WORKFLOW / STATUS
        if (requestField.type === Constant.FORM_TYPE.FIELD_WORKFLOW) {
            editItemData = buildEditFieldStatusItem(defaultFieldData);
            addNewItemList = addNewItemList.concat(editItemData.addNewItemList);
            requestFieldList = requestFieldList.concat(editItemData.editItemList);
        }

        // FIELD_TAG
        if (
            requestField.type === Constant.FORM_TYPE.FIELD_TAG &&
            field.structure.settingSelectItem !== Constant.SETTING_SELECT_ITEM_TAG_FIELD.MASTER_KNOWLEDGE &&
            field.structure.settingSelectItem !== Constant.SETTING_SELECT_ITEM_TAG_FIELD.AUTO_FILL &&
            parentPanelScreen !== Constant.FORM_MANAGEMENT_SCREEN.LIST_FIELD
        ) {
            editItemData = buildEditFieldTagItem(defaultFieldData);
            addNewItemList = addNewItemList.concat(editItemData.addNewItemList);
            requestFieldList = requestFieldList.concat(editItemData.editItemList);
        }


        // set usingProjects
        let usingProjects = field.usingProjects;
        if (field.clientFieldType === Constant.CLIENT_FIELD_TYPE.DUPLICATE_FIELD) {
            usingProjects = [projectDetail.id];
        }

        // add field
        // convert inputData to requestData
        delete requestField.isUsingProject;
        delete requestField.isErrorValidate;
        delete requestField.isProjectInuse;
        delete requestField.isShowInfo;
        delete requestField.clientFieldType;
        delete requestField.isErrorMinLargerMaxLength;
        delete requestField.itemList;
        delete requestField.defaultItemList;
        delete requestField.addToForm;
        delete requestField.errors;
        delete requestField.isUsingForm;
        delete requestField.updatedAt;
        delete requestField.orderObject;

        requestField.order = Utils.isTypeObject(requestField.order)
            ? JSON.stringify(requestField.order)
            : requestField.order;
        requestField.usingProjects = usingProjects;
        requestField.name = Utils.isTypeObject(requestField.name)
            ? JSON.stringify(requestField.name)
            : requestField.name;
        requestField.structure = Utils.isTypeObject(requestField.structure)
            ? JSON.stringify(requestField.structure)
            : requestField.structure;
        requestField.explanation = Utils.isTypeObject(requestField.explanation)
            ? JSON.stringify(requestField.explanation)
            : requestField.explanation;
        requestField.viewForCustomer = Utils.isTypeObject(requestField.viewForCustomer)
            ? JSON.stringify(requestField.viewForCustomer)
            : requestField.viewForCustomer;

        requestField.usingForms = Utils.isTypeObject(requestField.usingForms)
            ? JSON.stringify(requestField.usingForms)
            : requestField.usingForms;

        // mater
        switch (field.structure.settingSelectItem) {
            // master knowledge
            case Constant.SETTING_SELECT_ITEM_TAG_FIELD.MASTER_KNOWLEDGE:

                requestField.definition = Constant.FORM_DEFINITION.SYSTEM_MASTER_FIELD;

                let masterKnowledge = field.masterKnowledge;
                if (!Utils.isEmpty(masterKnowledge)) {
                    let structure = JSON.parse(requestField.structure);

                    // structure.formMaster = masterKnowledge.formMaster;
                    // structure.isAutoAddData = masterKnowledge.isAutoAddData;

                    requestField.relatedMasterForm = masterKnowledge.formMaster;
                    requestField.settingAutoAdd = masterKnowledge.isAutoAddData;

                    structure.listItem = masterKnowledge.listItem;

                    delete requestField.masterKnowledge;
                    requestField.structure = JSON.stringify(structure);
                }
                break;
            case Constant.SETTING_SELECT_ITEM_TAG_FIELD.AUTO_FILL:
                requestField.definition = Constant.FORM_DEFINITION.SYSTEM_MASTER_FIELD;

                let autoFillMasterField = field.autoFillMasterField;
                if (!Utils.isEmpty(autoFillMasterField)) {
                    let autoFillStructure = JSON.parse(requestField.structure);

                    // structure.formMaster = autoFillMasterField.formMaster;
                    // structure.isAutoAddData = autoFillMasterField.isAutoAddData;

                    requestField.relatedMasterForm = autoFillMasterField.formMaster;
                    requestField.settingAutoAdd = autoFillMasterField.isAutoAddData;
                    autoFillStructure.listItem = autoFillMasterField.listItem;
                    delete requestField.autoFillMasterField;
                    requestField.structure = JSON.stringify(autoFillStructure);
                }
                break;
            default:
                break;

        }

        // push to array
        requestFieldList.push(requestField);
    });

    return { requestFieldList, addNewItemList };
}

/**
 * onCreateTagFieldItem
 * @param {*} argsCommon
 * @param {*} tagField
 */
export const onCreateTagFieldItem = async (argsCommon, tagField, itemList) => {
    let itemDataList = [];
    let defaultItemList = [];

    await Promise.all(
        itemList.map(async (item) => {
            let requestTagItem = {
                id: item.id,
                usage: Constant.FORM_USAGE.TAG,
                name: JSON.stringify({
                    jp: {
                        A: 'F',
                        text: item.name,
                    },
                }),
                affiliationOrganization: tagField.affiliationOrganization,
                affiliationProject: tagField.affiliationProject,
                affiliationField: tagField.id,
                creator: tagField.creator,
                updator: tagField.updator,
                type: '',
                structure: JSON.stringify({
                    isShowItemInTicket: item.isCheckbox,
                    no: item.no,
                }),
                viewForCustomer: JSON.stringify({}),
                definition: Constant.FORM_DEFINITION.USER,
                explanation: JSON.stringify({}),
                usingProjects: tagField.usingProjects,
            };

            // call api add tag field item
            // let itemData = await createFormsData(argsCommon.API, requestTagItem);

            // push data
            itemDataList.push(generateItemFieldTag(requestTagItem));
            defaultItemList.push(requestTagItem);

            return requestTagItem;
        })
    );

    if (!Utils.isEmptyArray(itemDataList)) {
        itemDataList = sortItemListFieldByNo(itemDataList);
    }

    return { itemList: itemDataList, defaultItemList: defaultItemList };
};

/**
 * onCreateStatusFieldItem
 * @param {*} argsCommon
 * @param {*} statusField
 * @param {*} itemList
 */
export const onCreateStatusFieldItem = async (argsCommon, statusField, itemList) => {
    let itemDataList = [];
    let defaultItemList = [];

    await Promise.all(
        itemList.map(async (item) => {
            let statusItem = {
                id: item.id,
                usage: Constant.FORM_USAGE.STATUS,
                name: JSON.stringify(item.name),
                affiliationOrganization: statusField.affiliationOrganization,
                affiliationProject: statusField.affiliationProject,
                affiliationField: statusField.id,
                creator: statusField.creator,
                updator: statusField.updator,
                type: '',
                structure: JSON.stringify(item.structure),
                viewForCustomer: JSON.stringify(item.viewForCustomer),
                definition: Constant.FORM_DEFINITION.USER,
                explanation: JSON.stringify({}),
                usingProjects: [],
            };

            // call api add status field item
            // let itemData = await createFormsData(argsCommon.API, statusItem);

            // push data
            itemDataList.push(generateItemFieldStatus(statusItem));
            defaultItemList.push(statusItem);

            return statusItem;
        })
    );

    if (!Utils.isEmptyArray(itemDataList)) {
        itemDataList = sortItemListFieldByNo(itemDataList);
    }

    return { itemList: itemDataList, defaultItemList: defaultItemList };
};

/**
 * updateParentFieldList
 * If parent screen is list field, update in fieldList else parent is form managenment, update drag drop data
 * @param {*} props
 * @param {*} field
 * @param {*} newName
 * @param {*} isUsingProject
 * @returns
 */
export const updateParentFieldList = (props, field, newName, isUsingProject, newStructure) => {
    let fieldAddList = cloneDeep(props.fieldAddList);
    let indexInFieldAddList = Utils.findIndexInArrayById(field.id, fieldAddList);

    if (props.parentPanelScreen === Constant.FORM_MANAGEMENT_SCREEN.LIST_FIELD) {
        // push field to field list
        let fieldList = cloneDeep(props.fieldList);
        let indexInFieldList = Utils.findIndexInArrayById(field.id, fieldList);

        if (indexInFieldList !== -1) {
            fieldList[indexInFieldList].isShowInfo = false;
            fieldList[indexInFieldList].name = newName;
            fieldList[indexInFieldList].structure = Utils.isEmpty(newStructure) ? fieldList[indexInFieldList].structure : newStructure;
        } else {
            // fieldList = [fieldAddList[indexInFieldAddList]].concat([...props.fieldList]);
            fieldList = [...props.fieldList].concat([fieldAddList[indexInFieldAddList]]);
        }
        props.setFieldList(fieldList);
    } else if (
        [
            Constant.FORM_MANAGEMENT_SCREEN.ADD_FORM,
            Constant.FORM_MANAGEMENT_SCREEN.EDIT_FORM,
        ].includes(props.parentPanelScreen)
    ) {
        let listFieldAddToForm = !Utils.isEmptyArray(props.listFieldAddToForm)
            ? props.listFieldAddToForm
            : [];
        let listFieldInProject = !Utils.isEmptyArray(props.listFieldInProject)
            ? props.listFieldInProject
            : [];

        switch (props.regionDropIdBlock) {
            case '':
                listFieldAddToForm = [...listFieldAddToForm].concat([
                    fieldAddList[indexInFieldAddList],
                ]);
                break;
            case 'listFieldAddToForm':
                listFieldAddToForm = updateDragDropData(
                    props,
                    listFieldAddToForm,
                    field,
                    fieldAddList,
                    indexInFieldAddList,
                    newName,
                    isUsingProject,
                    newStructure
                );
                break;
            case 'listFieldInProject':
                listFieldInProject = updateDragDropData(
                    props,
                    listFieldInProject,
                    field,
                    fieldAddList,
                    indexInFieldAddList,
                    newName,
                    isUsingProject,
                    newStructure
                );
                break;
            default:
                break;
        }
        props.setListFieldAddToForm([...listFieldAddToForm]);
        props.setListFieldInProject([...listFieldInProject]);
    }
};

/**
 * updateDragDropData
 * @param {*} props
 * @param {*} data
 * @param {*} field
 * @param {*} fieldAddList
 * @param {*} indexInFieldAddList
 * @param {*} newName
 * @param {*} isUsingProject
 * @param {*} newStructure
 * @returns data updated
 */
const updateDragDropData = (
    props,
    data,
    field,
    fieldAddList,
    indexInFieldAddList,
    newName,
    isUsingProject,
    newStructure
) => {
    if (Utils.findIndexInArrayById(field.id, data) !== -1) {
        let indexInData = Utils.findIndexInArrayById(field.id, data);
        data[indexInData].name = newName;
        data[indexInData].usingProjects = isUsingProject ? [props.projectDetail.id] : [];
        data[indexInData].isUsingProject = isUsingProject;
        data[indexInData].isErrorValidate = false;
        data[indexInData].isShowInfo = false;
        data[indexInData].structure = Utils.isEmpty(newStructure) ? data[indexInData].structure : newStructure;
    } else {
        // data = [fieldAddList[indexInFieldAddList]].concat([...data]);
        data = [...data].concat(fieldAddList[indexInFieldAddList]);
    }

    return data;
};

/**
 *
 * @param {*} arr
 * @returns new arr
 */
export const sortItemListFieldByNo = (arr) => {
    try {
        if (Utils.isEmptyArray(arr)) {
            return [];
        }
        return arr.sort((a, b) => {
            return new Date(a.structure.no) - new Date(b.structure.no);
        });
    } catch (err) {
        return arr;
    }
};

/**
* @param {*} field 
* @returns 
*/
export const validateNumberField = (field, isValidateError, fieldListInState) => {
    let minValue = field.structure.minValue;
    let maxValue = field.structure.maxValue;
    if (!Utils.isEmptyString(minValue)) {
        if (
            minValue > Constant.NUMBER_FIELD_DEFAULT.MAX_VALUE ||
            minValue < Constant.NUMBER_FIELD_DEFAULT.MIN_VALUE
        ) {
            field.errors.minValue = {
                message: '[-2000000000~2000000000]の数値範囲内で入力してください。',
                type: 'outOfRangeError',
            };
            isValidateError = true;
            field.isShowInfo = true;
        } else {
            if (!Utils.isEmptyString(maxValue) && minValue > maxValue) {
                field.errors.minValue = {
                    message: '最小値≦最大値で入力してください。',
                    type: 'minLargerMaxError',
                };
                isValidateError = true;
                field.isShowInfo = true;
            } else {
                if (!isValidateError && !Utils.isEmptyObject(field.errors.minValue)) {
                    field.errors.minValue = {};
                    isValidateError = false;
                }
            }
        }
    } else {
        if (!isValidateError && !Utils.isEmptyObject(field.errors.minValue)) {
            field.errors.minValue = {};
            isValidateError = false;
        }
    }

    if (!Utils.isEmptyString(maxValue)) {
        if (
            maxValue > Constant.NUMBER_FIELD_DEFAULT.MAX_VALUE ||
            maxValue < Constant.NUMBER_FIELD_DEFAULT.MIN_VALUE
        ) {
            field.errors.maxValue = {
                message: '[-2000000000~2000000000]の数値範囲内で入力してください。',
                type: 'outOfRangeError',
            };
            isValidateError = true;
            field.isShowInfo = true;
        } else {
            if (!isValidateError && !Utils.isEmptyObject(field.errors.maxValue)) {
                field.errors.maxValue = {};
                isValidateError = false;
            }
        }
    }


    if (!Utils.isEmptyArray(fieldListInState) && !isAddNewField(field) && field.hasValue) {
        let indexInFieldListState = Utils.findIndexInArrayById(field.id, fieldListInState);
        let fieldInState = fieldListInState[indexInFieldListState];
        let maxCurrent = fieldInState.structure.maxValue;
        let minCurrent = fieldInState.structure.minValue;

        // check min
        let isErrorMinValue = minCurrent < minValue;
        // if minCurrent < inputValue then error
        if (isErrorMinValue) {
            field.errors.minValue = {
                message: `${minCurrent}以下の値で入力してください。`,
                type: "minValue"
            }
            isValidateError = true;
            field.isShowInfo = true;
        }

        // check max
        let isErrorMaxValue = maxCurrent > maxValue;
        // if maxCurrent > inputValue then error
        if (isErrorMaxValue) {
            field.errors.maxValue = {
                message: `${maxCurrent}以上の値で入力してください。`,
                type: "maxValue"
            }
            isValidateError = true;
            field.isShowInfo = true;
        }
    }


    return { isValidateError, field };
};

/**
* Validate text fields
* @param {*} field 
* @returns 
*/
export const validateTextField = (field, isValidateError, fieldListInState, fieldType) => {
    let minLength = field.structure.minLength;
    let maxLength = field.structure.maxLength;
    let isRichTextField = false;
    let minLengthCanSetting = 0;
    let maxLengthCanSetting = Constant.TEXT_FIELD_DEFAULT.MAX_LENGTH;

    if (fieldType && fieldType === Constant.FORM_TYPE.FIELD_RICHTEXT) {
        maxLengthCanSetting = Constant.RICHTEXT_MAX_LENGTH;
        isRichTextField = true;
    }

    if (isRichTextField && !maxLength && maxLength !== 0) {
        field.errors.maxLength = {
            message: `[${minLengthCanSetting}~${maxLengthCanSetting}]の数値範囲内で入力してください。`,
            type: 'outOfRangeError',
        };
        isValidateError = true;
        field.isShowInfo = true;
    }

    if (!Utils.isEmptyString(minLength)) {
        if (
            minLength > maxLengthCanSetting ||
            minLength < minLengthCanSetting
        ) {
            field.errors.minLength = {
                message: `[0~${maxLengthCanSetting}]の数値範囲内で入力してください。`,
                type: 'outOfRangeError',
            };
            isValidateError = true;
            field.isShowInfo = true;
        } else {
            if (!Utils.isEmptyString(maxLength) && minLength > maxLength) {
                field.errors.minLength = {
                    message: '最小値≦最大値で入力してください。',
                    type: 'minLargerMaxError',
                };
                isValidateError = true;
                field.isShowInfo = true;
            } else {
                if (!isValidateError && !Utils.isEmptyObject(field.errors.minLength)) {
                    field.errors.minLength = {};
                    isValidateError = false;
                }
            }
        }
    } else {
        if (!isValidateError && !Utils.isEmptyObject(field.errors.minLength)) {
            field.errors.minLength = {};
            isValidateError = false;
        }
    }

    if (!Utils.isEmptyString(maxLength)) {
        if (
            maxLength > maxLengthCanSetting ||
            maxLength < minLengthCanSetting
        ) {
            field.errors.maxLength = {
                message: `[0~${maxLengthCanSetting}]の数値範囲内で入力してください。`,
                type: 'outOfRangeError',
            };
            isValidateError = true;
            field.isShowInfo = true;
        } else {
            if (!isValidateError && !Utils.isEmptyObject(field.errors.maxLength)) {
                field.errors.maxLength = {};
                isValidateError = false;
            }
        }
    }

    if (!Utils.isEmptyArray(fieldListInState) && !isAddNewField(field) && field.hasValue) {
        let indexInFieldListState = Utils.findIndexInArrayById(field.id, fieldListInState);
        let fieldInState = fieldListInState[indexInFieldListState];
        let maxCurrent = fieldInState.structure.maxLength;
        let minCurrent = fieldInState.structure.minLength;

        // check min
        let isErrorMinValue = minCurrent < minLength;
        // if minCurrent < inputValue then error
        if (isErrorMinValue) {
            field.errors.minLength = {
                message: `${minCurrent}以下の値で入力してください。`,
                type: "minLength"
            }
            isValidateError = true;
            field.isShowInfo = true;
        }

        // check max
        let isErrorMaxValue = maxCurrent > maxLength;
        // if maxCurrent > inputValue then error
        if (isErrorMaxValue) {
            field.errors.maxLength = {
                message: `${maxCurrent}以上の値で入力してください。`,
                type: "maxLength"
            }
            isValidateError = true;
            field.isShowInfo = true;
        }
    }
    return { isValidateError, field };
};

export const validateFieldList = (fieldListInput, isValidateError, fieldListInState) => {
    if(Utils.isEmptyArray(fieldListInput)) return { fieldListInput, isValidateError };

    let fieldList = cloneDeep(fieldListInput);
    fieldList?.forEach((originField, index) => {
        let field = {...originField}
        field.errors = {};

        let name = Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, field.name);
        if (Utils.isEmptyString(name)) {
            field.errors.name = {
                message: 'フィールド名をご記入ください。',
                type: 'require',
            };
            field.isShowInfo = true;
            isValidateError = true;
        } else {
            if (name.length > Constant.MAX_LENGTH.FIELD_NAME) {
                field.errors.name = {
                    message: `${Constant.MAX_LENGTH.FIELD_NAME}文字の以下で入力してください。`,
                    type: 'maxLength',
                };
                field.isShowInfo = true;
                isValidateError = true;
            } else {
                if (!isValidateError && !Utils.isEmptyObject(field.errors.name)) {
                    field.errors.name = {};
                    isValidateError = false;
                }
            }
        }

        let validateResult;
        switch (field.type) {
            case Constant.FORM_TYPE.FIELD_INT:
                validateResult = validateNumberField(field, isValidateError, fieldListInState);
                field = validateResult.field;
                isValidateError = validateResult.isValidateError;
                break;
            case Constant.FORM_TYPE.FIELD_RICHTEXT:
            case Constant.FORM_TYPE.FIELD_TEXT:
                validateResult = validateTextField(field, isValidateError, fieldListInState, field.type);
                field = validateResult.field;
                isValidateError = validateResult.isValidateError;
                break;
            default:
                break;
        }
        fieldList[index] = field
    });
    return { fieldList, isValidateError };
};

export const onChangeItemInForm = (args, field, regionDropId, isUsingForm) => {
    let listFieldAddToForm = [...args.listFieldAddToForm];
    let listFieldInProject = [...args.listFieldInProject];

    if (regionDropId) {
        let indexInFistFieldInProject = Utils.findIndexInArrayById(field.id, listFieldInProject);
        let indexInListFieldAddToForm = Utils.findIndexInArrayById(field.id, listFieldAddToForm);

        if (indexInFistFieldInProject !== -1) {
            let index = listFieldInProject.findIndex((x) => x.id === field.id);
            let fieldUpdate = listFieldInProject[index];

            fieldUpdate.isUsingForm = isUsingForm;
            listFieldInProject.splice(index, 1);
            args.setListFieldInProject(listFieldInProject);

            listFieldAddToForm.push(fieldUpdate);
            args.setListFieldAddToForm(listFieldAddToForm);
        }

        if (indexInListFieldAddToForm !== -1) {
            let index = listFieldAddToForm.findIndex((x) => x.id === field.id);
            let fieldUpdate = listFieldAddToForm[index];
            fieldUpdate.isUsingForm = isUsingForm;
            listFieldAddToForm.splice(index, 1);
            args.setListFieldAddToForm(listFieldAddToForm);

            listFieldInProject.push(fieldUpdate);
            args.setListFieldInProject(listFieldInProject);
        }

        // Update addToForm option if field exist in fieldAddList
        let indexInFieldAddList = Utils.findIndexInArrayById(field.id, args.fieldAddList);
        if (indexInFieldAddList !== -1) {
            let currentFieldAddList = cloneDeep(args.fieldAddList);
            currentFieldAddList[indexInFieldAddList].addToForm =
                regionDropId === 'listFieldInProject' ? true : false;
            currentFieldAddList[indexInFieldAddList].isUsingForm = isUsingForm;
            args.setFieldAddList(currentFieldAddList);
        }
    }


}

export const isFieldError = (fieldName, currentDataField) => {
    return (
        currentDataField.errors &&
        currentDataField.errors[fieldName] &&
        currentDataField.errors[fieldName].message
    );
};

export const getErrorByField = (fieldName, currentDataField) => {
    return currentDataField.errors && currentDataField.errors[fieldName]
        ? currentDataField.errors[fieldName].message
        : '';
};

export const validateNameOfField = (value) => {
    let errors = {};
    if (Utils.isEmptyString(value)) {
        errors = {
            message: 'フィールド名をご記入ください。',
            type: 'require',
        };
    } else {
        if (value.length > Constant.MAX_LENGTH.FIELD_NAME) {
            errors = {
                message: `${Constant.MAX_LENGTH.FIELD_NAME}文字の以下で入力してください。`,
                type: 'maxLength',
            };
        } else {
            errors = {};
        }
    }

    return errors;
}

export const validateMinLengthOfTextField = (minLength, maxLength, minCurrent, hasValue, fieldType) => {
    let errors = {};
    let maxLengthCanSetting = Constant.TEXT_FIELD_DEFAULT.MAX_LENGTH; 
    if (fieldType && fieldType === Constant.FORM_TYPE.FIELD_RICHTEXT) {
        maxLengthCanSetting = Constant.RICHTEXT_MAX_LENGTH;
    }

    if (!Utils.isEmptyString(minLength)) {
        //case edit field
        if (!Utils.isEmpty(hasValue) && minLength > minCurrent && minLength <= maxLengthCanSetting) {
            errors = {
                message: `${minCurrent}以下の値で入力してください。`,
                type: 'outOfRangeError',
            };
        } else if (
            minLength > maxLengthCanSetting ||
            minLength < Constant.TEXT_FIELD_DEFAULT.MIN_LENGTH
        ) {
            errors = {
                message: `[0~${maxLengthCanSetting}]の数値範囲内で入力してください。`,
                type: 'outOfRangeError',
            };
        } else if (!Utils.isEmptyString(maxLength) && minLength > maxLength) {
            errors = {
                message: '最小値≦最大値で入力してください。',
                type: 'minLargerMaxError',
            };
        } else {
            errors = {};
        }
    } else {
        errors = {};
    }

    return errors;
}

export const validateMaxLengthOfTextField = (maxLength, minLength, maxCurrent, hasValue, fieldType) => {
    let errors = {};
    let maxLengthCanSetting = Constant.TEXT_FIELD_DEFAULT.MAX_LENGTH;
    let minLengthCanSetting = 0;
    let isRichTextField = false;
    if (fieldType && fieldType === Constant.FORM_TYPE.FIELD_RICHTEXT) {
        maxLengthCanSetting = Constant.RICHTEXT_MAX_LENGTH;
        isRichTextField = true;
    }

    if (isRichTextField && !maxLength && maxLength !== 0) {
        return {
            message: `[${minLengthCanSetting}~${maxLengthCanSetting}]の数値範囲内で入力してください。`,
            type: 'outOfRangeError'
        }
    }

    if (!Utils.isEmptyString(maxLength)) {
        //case edit field
        if (!Utils.isEmpty(hasValue) && maxLength < maxCurrent && maxLength >= minLengthCanSetting) {
            errors = {
                message: `${maxCurrent}以上の値で入力してください。`,
                type: 'outOfRangeError',
            };
        } else if (
            maxLength > maxLengthCanSetting ||
            maxLength < minLengthCanSetting
        ) {
            errors = {
                message: `[${minLengthCanSetting}~${maxLengthCanSetting}]の数値範囲内で入力してください。`,
                type: 'outOfRangeError',
            };
        } else if (!Utils.isEmptyString(minLength) && minLength > maxLength) {
            errors = {
                message: '最小値≦最大値で入力してください。',
                type: 'minLargerMaxError',
            };
        } else {
            errors = {};
        }
    } else {
        errors = {};
    }

    return errors;
}

export const validateMinValueOfNumberField = (minValue, maxValue, minCurrent, hasValue) => {
    let errors = {};

    if (!Utils.isEmptyString(minValue)) {
        //case edit field
        if (!Utils.isEmpty(hasValue) && minValue > minCurrent && minValue <= Constant.NUMBER_FIELD_DEFAULT.MAX_VALUE) {
            errors = {
                message: `${minCurrent}以下の値で入力してください。`,
                type: 'outOfRangeError',
            };
        } else if (
            minValue > Constant.NUMBER_FIELD_DEFAULT.MAX_VALUE ||
            minValue < Constant.NUMBER_FIELD_DEFAULT.MIN_VALUE
        ) {
            errors = {
                message: '[-2000000000~2000000000]の数値範囲内で入力してください。',
                type: 'outOfRangeError',
            };
        } else if (!Utils.isEmpty(maxValue) && minValue > maxValue) {
            errors = {
                message: '最小値≦最大値で入力してください。',
                type: 'minLargerMaxError',
            };
        } else {
            errors = {};
        }
    } else {
        errors = {};
    }

    return errors;
}

export const validateMaxValueOfNumberField = (maxValue, minValue, maxCurrent, hasValue) => {
    let errors = {};

    if (!Utils.isEmptyString(maxValue)) {
        //case edit field
        if (!Utils.isEmpty(hasValue) && maxValue < maxCurrent && maxValue >= Constant.NUMBER_FIELD_DEFAULT.MIN_VALUE) {
            errors = {
                message: `${maxCurrent}以上の値で入力してください。`,
                type: 'outOfRangeError',
            };
        } else if (
            maxValue > Constant.NUMBER_FIELD_DEFAULT.MAX_VALUE ||
            maxValue < Constant.NUMBER_FIELD_DEFAULT.MIN_VALUE
        ) {
            errors = {
                message: '[-2000000000~2000000000]の数値範囲内で入力してください。',
                type: 'outOfRangeError',
            };
        } else if (!Utils.isEmpty(minValue) && minValue > maxValue) {
            errors = {
                message: '最小値≦最大値で入力してください。',
                type: 'minLargerMaxError',
            };
        } else {
            errors = {};
        }
    } else {
        errors = {};
    }

    return errors;
}

export const onChangeFieldName = (event, argsAll) => {
    let textValue = event.target.value.trim();

    // Update in fieldAddList
    let cloneFieldAddList = [...argsAll.fieldAddList];
    let newName = {
        jp: {
            A: 'F',
            text: textValue
        }
    };
    cloneFieldAddList[argsAll.indexAddField].name = {
        jp: {
            A: 'F',
            text: textValue
        }
    };
    cloneFieldAddList[argsAll.indexAddField].usingProjects = argsAll.isUsingProject
        ? [argsAll.projectDetail.id]
        : [];
    cloneFieldAddList[argsAll.indexAddField].isUsingProject = argsAll.isUsingProject;

    let validateResult = validateNameOfField(textValue);
    if (!Utils.isEmptyObject(validateResult)) {
        argsAll?.setIsChangeData(false);
    }
    let errors = cloneFieldAddList[argsAll.indexAddField].errors
        ? cloneFieldAddList[argsAll.indexAddField].errors
        : {};
    errors.name = validateResult;
    cloneFieldAddList[argsAll.indexAddField].errors = errors;
    argsAll.setFieldAddList(cloneFieldAddList);
    if (argsAll.requiredFields) {
        // const isUnitFieldEnter = argsAll.checkUnitFieldEnter();
        if (textValue.length !== 0) {
            argsAll.setIsChangeData(true);
        } else {
            argsAll.setIsChangeData(false);
        }
    } else {
        if (textValue.length !== 0) {
            argsAll.setIsChangeData(true);
        } else {
            argsAll.setIsChangeData(false);
        }
    }
    updateDataOnChangeFieldName(argsAll, cloneFieldAddList[argsAll.indexAddField], newName);
};

/**
 * This function is used to validated textfield
 * @param {*} textValue 
 * @param {*} argsAll 
 * @returns 
 */

export const validateAddFieldNameCustom = (textValue, argsAll) => {
    let cloneFieldAddList = [...argsAll.fieldAddList];
    let validateResult = validateNameOfField(textValue);
    let errors = cloneFieldAddList[argsAll.indexAddField].errors
        ? cloneFieldAddList[argsAll.indexAddField].errors
        : {};

    errors.name = validateResult;
    cloneFieldAddList[argsAll.indexAddField].errors = errors;
    argsAll.setFieldAddList(cloneFieldAddList);
    return !Utils.isEmptyObject(errors.name);
}

export const updateDataOnChangeFieldName = (props, currentFieldData, newName, isUpdateFieldList = true) => {
    if (props.parentPanelScreen === Constant.FORM_MANAGEMENT_SCREEN.LIST_FIELD) {
        // push field to field list
        let fieldList = cloneDeep(props.fieldList);
        let indexInFieldList = Utils.findIndexInArrayById(currentFieldData.id, fieldList);
        if (indexInFieldList !== -1) {
            fieldList[indexInFieldList].isShowInfo = true;
            fieldList[indexInFieldList].name = newName;
            if (isUpdateFieldList) {
                props.setFieldList(fieldList);
            } else {
                props.setIsChangeData(true);
            }
            
        }
    }
}

/**
 * Check isOwnerProject
 * @param {*} field
 * @param {*} userDetail
 * @param {*} projectDetail
 * @returns isOwnerProject
 */
export const isOwnerProject = (field, userDetail, projectDetail) => {

    // check field shared
    if(projectDetail.affiliationOrganization !== field.affiliationOrganization){
        return false
    }
    // if user admin then owner project
    if ((Constant.PRIVILEGE_USER.ADMIN_OWNER === userDetail.privilege ||
        Constant.PRIVILEGE_USER.ADMIN === userDetail.privilege)) {
        return true;
    }

    // check project manager with project inuse
    if (projectDetail.isProjectManager && field.affiliationProject === projectDetail.id) {
        return true;
    }
    return false;
};

/**
 * Sort projectList by dictionary and projectInuse
 * @param {*} projectInuse
 * @param {*} projectList
 * @returns projectList
 */
export const sortProjectByprojectInuseAndDictionary = (projectInuse, projectList) => {
    try {
        let projectListClone = cloneDeep(projectList);
        let indexOfProjectInuse = Utils.findIndexInArrayById(projectInuse.id, projectListClone);
        let projectInuseInList = cloneDeep(projectListClone[indexOfProjectInuse]);
        projectListClone = Utils.removeItemInArrayById(projectInuse.id, projectListClone);

        projectListClone = sortListByDictionary(projectListClone);
        let projectSorted = [projectInuseInList].concat(projectListClone);
        projectSorted = projectSorted?.filter(projectItem => !Utils.isEmptyObject(projectItem));
        return projectSorted;
    } catch (error) {
        console.log(error);
        return [];
    }
};


export const buildEditFieldTagItem = (field) => {
    let addNewItemList = [];
    let editItemList = [];
    let currentItemList = cloneDeep(field.itemList);
    let defaultItemList = cloneDeep(field.defaultItemList);
    if (!Utils.isEmptyArray(currentItemList)) {
        currentItemList.forEach(item => {
            let itemRequest = cloneDeep(item);

            delete itemRequest.errorMsg;
            delete itemRequest.isErrorValidate;
            delete itemRequest.isShowInfo;
            delete itemRequest.isAutoFocusCustmerView;
            delete itemRequest.isAutoFocusItemName;
            delete itemRequest.isCheckbox;
            delete itemRequest.isEditName;
            delete itemRequest.no;
            delete itemRequest.isAutoFocus;

            // for compare default item
            let itemRequestOriginName;
            let itemRequestOriginStructure;
            // name
            if (typeof itemRequest.name === 'object') {
                itemRequest.name = JSON.stringify(itemRequest.name);
                itemRequestOriginName = Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, itemRequest.name);
            } else {
                let name = {
                    jp: {
                        A: 'F',
                        text: item.name,
                    },
                };
                itemRequest.name = JSON.stringify(name);
                itemRequestOriginName = item.name;
            }
            // structure
            if ((itemRequest.structure  && typeof itemRequest.structure === 'object') || (itemRequest.structure  === "null")) {
                itemRequest.structure = JSON.stringify(itemRequest.structure);
                itemRequestOriginStructure = itemRequest.structure;
            } else {
                let structure = {
                    isShowItemInTicket: item.isCheckbox,
                    no: item.no
                };
                itemRequest.structure = JSON.stringify(structure);
                itemRequestOriginStructure = structure;
            }

            let indexInDefault = Utils.findIndexInArrayById(itemRequest.id, defaultItemList);
            if (indexInDefault === -1) {
                addNewItemList.push({ item: itemRequest, field: field });
            } else {
                const defaultItem = defaultItemList[indexInDefault];
                const defaultItemName = defaultItem?.name;
                const defaultItemStructure = Utils.convertJSONDataToObject(defaultItem.structure);
                // compare name
                if (defaultItemName !== itemRequestOriginName ||
                    defaultItemStructure.isShowItemInTicket !== itemRequestOriginStructure.isShowItemInTicket ||
                    defaultItemStructure.no !== itemRequestOriginStructure.no
                ) {
                    editItemList.push(itemRequest);
                }
            }
        });
    }

    return { addNewItemList, editItemList }
}


export const buildEditFieldStatusItem = (field) => {
    let addNewItemList = [];
    let editItemList = [];
    let currentItemList = cloneDeep(field.itemList);
    let defaultItemList = cloneDeep(field.defaultItemList);
   
    if (!Utils.isEmptyArray(currentItemList)) {
        currentItemList.forEach(item => {
            delete item.errorMsg;
            delete item.isErrorValidate;
            delete item.isShowInfo;
            delete item.isAutoFocusCustmerView;
            delete item.isAutoFocusItemName;

            item.name = typeof item.name === 'object' ? JSON.stringify(item.name) : item.name;
            item.structure = typeof item.structure === 'object' ? JSON.stringify(item.structure) : item.structure;
            item.viewForCustomer = typeof item.viewForCustomer === 'object' ? JSON.stringify(item.viewForCustomer) : item.viewForCustomer;

            let indexInDefault = Utils.findIndexInArrayById(item.id, defaultItemList);
            if (indexInDefault === -1) {
                addNewItemList.push({ item, field });
            } else {
                // check has edit 
                
                const defaultItem = defaultItemList[indexInDefault];
                if (defaultItem.name !== item.name ||
                    defaultItem.structure !== item.structure ||
                    defaultItem.viewForCustomer !== item.viewForCustomer
                ) {
                    editItemList.push(item);
                }
            }
        });
    }

    return { addNewItemList, editItemList }
}
/**
 * Check permission edit field
 * @param {*} currentFieldData 
 * @param {*} argsCommon 
 * @returns isEditField
 */
export const isEditField = (currentFieldData, argsCommon) => {
    let isOwner = isOwnerProject(currentFieldData, argsCommon.userDetail, argsCommon.projectDetail);
    if (!isOwner) {
        return false;
    }
    
    if (currentFieldData.affiliationProject !== argsCommon.projectDetail.id) {
        return false;
    }

    if (
        argsCommon.parentPanelScreen === Constant.FORM_MANAGEMENT_SCREEN.LIST_FIELD ||
        (argsCommon.parentPanelScreen !== Constant.FORM_MANAGEMENT_SCREEN.LIST_FIELD &&
            isAddNewField(currentFieldData))
    ) {
        return true;
    }
    return false;
};



/**
 * Check use item in ticket
 * @param {*} item 
 * @param {*} field
 * @returns isUse
 */
export const isUseItemInTicket = (item, field) => {
    if (Utils.getPrefixOfId(item?.id) === "tag") return false;

    if (Utils.isEmpty(item) || Utils.isEmpty(field) || Utils.isEmpty(field.defaultItemList)) {
        return false;
    }
    let index = Utils.findIndexInArrayById(item.id, field.defaultItemList);
    if (index === -1) {
        return false;
    }
    return Utils.isEmpty(field.defaultItemList[index].hasValue) ? false : field.defaultItemList[index].hasValue;
};


/**
 * Check use/unuse of field in form
 * @param {*} field
 * @param {*} formList
 * @param {*} argsCommon
 * @returns isUse
 */
export const isUseFieldInForm = (field, formList, argsCommon) => {
    if (Utils.isEmpty(field) || Utils.isEmpty(formList)) {
        return false;
    }

    // if field is system field or create field in screen create form  then auto use
    if (
        field.definition === Constant.FORM_DEFINITION.SYSTEM ||
        [
            Constant.FORM_MANAGEMENT_SCREEN.ADD_FORM,
            Constant.FORM_MANAGEMENT_SCREEN.EDIT_FORM,
        ].includes(argsCommon.parentPanelScreen)
    ) {
        return true;
    }

    if (!Utils.isEmptyObject(field.usingForms)) {
        for (const [key, value] of Object.entries(field.usingForms)) {
            let indexInFormList = Utils.findIndexInArrayById(key, formList);
            /* check usingForms when field shared */
            // if (indexInFormList !== -1 && value.active === Constant.FIELD_USING_FORMS.ACTIVE) {
            //     return true;
            // }
            if ( value.active === Constant.FIELD_USING_FORMS.ACTIVE) {
                   return true;
            }
        }
        return false;
    }
    return false;
};


/**
 * Check permission delete field 
 * @param {*} field 
 * @param {*} formList 
 * @param {*} argsCommon 
 * @returns isPermission
 */
export const isPermissionDeleteField = (field, formList, argsCommon) => {
    if (Utils.isEmpty(field)) {
        return false;
    }

    // check field use in form
    let isUseInForm = isUseFieldInForm(field, formList, argsCommon);
    if (isUseInForm) {
        return false;
    }
    
    // check owner project
    let userDetail = argsCommon.userDetail;
    let projectDetail = argsCommon.projectDetail;
    
    // check field shared
    if(projectDetail.affiliationOrganization !== field.affiliationOrganization){
        return false
    }
    let isOwner = isOwnerProject(field, userDetail, projectDetail);
    return isOwner;
}

/**
 * check is field is input value in form
 * @param {*} field 
 * @param {*} formData 
 * @returns status
 */
export const isInputFieldValueInForm = (field, formData) => {
    if (Utils.isEmptyObject(field) || Utils.isEmptyObject(formData)) {
        return false;
    }

    if (
        field.definition === Constant.FORM_DEFINITION.SYSTEM ||
        (
            !Utils.isEmptyObject(field.usingForms) &&
            field.usingForms.hasOwnProperty(formData.id) &&
            field.usingForms[formData.id].hasValue
        )
    ) {
        return true;
    }

    return false;
};

export const updateFieldListOnchangeData = (props, field) => {
    let fieldList = cloneDeep(props.fieldList);
    let indexInFieldList = Utils.findIndexInArrayById(field.id, fieldList);
    if (indexInFieldList !== -1) {
        fieldList[indexInFieldList].isShowInfo = true;
        fieldList[indexInFieldList].structure = field.structure;
    }
    props.setFieldList(fieldList);

    // case create/edit form
    if (
        [
            Constant.FORM_MANAGEMENT_SCREEN.ADD_FORM,
            Constant.FORM_MANAGEMENT_SCREEN.EDIT_FORM,
        ].includes(props.parentPanelScreen)
    ) {
        let listFieldAddToForm = !Utils.isEmptyArray(props.listFieldAddToForm)
            ? cloneDeep(props.listFieldAddToForm)
            : [];
        let listFieldInProject = !Utils.isEmptyArray(props.listFieldInProject)
            ? cloneDeep(props.listFieldInProject)
            : [];

        if (Utils.findIndexInArrayById(field.id, listFieldAddToForm) !== -1) {
            let indexInData = Utils.findIndexInArrayById(field.id, listFieldAddToForm);
            listFieldAddToForm[indexInData].isShowInfo = true;
            listFieldAddToForm[indexInData].structure = field.structure;

            props.setListFieldAddToForm(listFieldAddToForm);
        }
        if (Utils.findIndexInArrayById(field.id, listFieldInProject) !== -1) {
            let indexInData = Utils.findIndexInArrayById(field.id, listFieldInProject);
            listFieldInProject[indexInData].isShowInfo = true;
            listFieldInProject[indexInData].structure = field.structure;

            props.setListFieldInProject(listFieldInProject);
        }
    }
}

export const checkIsUpdateField = argsCommon => {
    return (
        !Utils.isEmptyArray(argsCommon.fieldLisForUpdate) ||
        !Utils.isEmptyArray(argsCommon.fieldList) ||
        !Utils.isEmptyArray(argsCommon.fieldList)
    );
};

export const checkDisableBtnSubmit = argsCommon => {
    if (Utils.isEmptyArray(argsCommon.fieldAddList)) {
        if (checkIsUpdateField(argsCommon)) {
            argsCommon.setIsDisableBtnSubmit(false);
        } else {
            argsCommon.setIsDisableBtnSubmit(true);
        }
        return;
    }
    if (Utils.isEmptyObject(validateFieldInput(argsCommon))) {
        argsCommon.setIsDisableBtnSubmit(false);
    } else {
        argsCommon.setIsDisableBtnSubmit(true);
    }
};

export const getNumberFormStrings = (type, stringsInput) => {
    let regex = '';
    let matches;
    let number;
    switch (type) {
        case 'POSITIVE_INTEGER':
            regex = /\d+/g;
            matches = stringsInput.match(regex);
            if (matches) {
                number = matches.join('');
            }
            return number ? Number(number) : '';
        case 'NEGATIVE_INTEGER':
            if (stringsInput === '-') return stringsInput;
            regex = /-?\d+/g;
            matches = stringsInput.match(regex);
            if (matches) {
                number = matches.join('');
            }
            return number ? Number(number) : '';
        default:
            break;
    }
};

export const updateFormMinNumber = (props, enterName, value, indexAddField, valueType) => {
    const newFieldAddList =  [...props.fieldAddList];

    if (valueType === 'value') {
        newFieldAddList[indexAddField].structure.minValue = value;
    }

    if (valueType === 'length') {
        newFieldAddList[indexAddField].structure.minLength = value;
    }

    props.setFieldAddList(newFieldAddList);
    // if (Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, enterName).length > 0) {
        props.setIsChangeData(true);
    // }
};

export const updateFormMaxNumber = (props, enterName, value, indexAddField, valueType) => {
    const newFieldAddList = [...props.fieldAddList];

    if (valueType === 'value') {
        newFieldAddList[indexAddField].structure.maxValue = value;
    }

    if (valueType === 'length') {
        newFieldAddList[indexAddField].structure.maxLength = value;
    }

    props.setFieldAddList(newFieldAddList);
    // if (Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, enterName).length > 0) {
        props.setIsChangeData(true);
    // }
};

export const updateFormRecordNumber = (props, enterName, value, indexAddField) => {
    const newFieldAddList = [...props.fieldAddList];
    newFieldAddList[indexAddField].structure.recordNumber = Number(value) < 0 ? 0 : Number(value);
    props.setFieldAddList(newFieldAddList);
    // if (Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, enterName).length > 0) {
    props.setIsChangeData(true);
    // }
}

/**
 * Get icon path by type field
 * @param {*} field 
 * @returns iconPath
 */
export const getIconFieldByTypeField = field => {
    let iconPath = '';

    let typeField =
        field?.definition === Constant.FORM_DEFINITION.SYSTEM &&
        !Object.values(Constant.DRILLDOWN_SYSTEM_FIELD).includes(field.sysField)
            ? field.sysField
            : field.type;

    switch (typeField) {
        // system field
        // guestOrg
        case 'guestOrg':
            iconPath = '/assets/image/icon-field02.svg';
            break;
        // guestProject
        case 'guestProject':
            iconPath = '/assets/image/icon-field02.svg';
            break;
        // adminMailAdress
        case 'adminMailAdress':
            iconPath = '/assets/image/icon-field02.svg';
            break;
        // adminLastName
        case 'adminLastName':
            iconPath = '/assets/image/icon-field02.svg';
            break;
        // adminFirstName
        case 'adminFirstName':
            iconPath = '/assets/image/icon-field02.svg';
            break;
        // outline
        case 'outline':
            iconPath = '/assets/image/icon-field02.svg';
            break;
        // category
        case 'category':
            iconPath = '/assets/image/icon-field02.svg';
            break;
        // description
        case 'description':
            iconPath = '/assets/image/icon-field02.svg';
            break;
        // attachments
        case 'attachments':
            iconPath = '/assets/image/icon-field08.svg';
            break;
        // field_qa_category
        case 'field_qa_category':
            iconPath = '/assets/image/icon-field02.svg';
            break;
        // field_qa_category_tag_requirement
        case 'field_qa_category_tag_requirement':
            iconPath = '/assets/image/icon-field0.svg';
            break;
        // field_qa_category_tag_price
        case 'field_qa_category_tag_price':
            iconPath = '/assets/image/icon-field0.svg';
            break;
        // field_qa_category_tag_quotation
        case 'field_qa_category_tag_quotation':
            iconPath = '/assets/image/icon-field0.svg';
            break;
        // field_qa_category_tag_cancel
        case 'field_qa_category_tag_cancel':
            iconPath = '/assets/image/icon-field0.svg';
            break;
        // field_qa_category_tag_shipping_delivery
        case 'field_qa_category_tag_shipping_delivery':
            iconPath = '/assets/image/icon-field0.svg';
            break;
        // field_qa_category_tag_payment
        case 'field_qa_category_tag_payment':
            iconPath = '/assets/image/icon-field0.svg';
            break;
        // field_qa_category_tag_business_day
        case 'field_qa_category_tag_business_day':
            iconPath = '/assets/image/icon-field0.svg';
            break;
        // field_qa_category_tag_operation_setting_method
        case 'field_qa_category_tag_operation_setting_method':
            iconPath = '/assets/image/icon-field0.svg';
            break;
        // field_qa_category_tag_troubleshooting
        case 'field_qa_category_tag_troubleshooting':
            iconPath = '/assets/image/icon-field0.svg';
            break;
        // field_qa_category_tag_order
        case 'field_qa_category_tag_order':
            iconPath = '/assets/image/icon-field0.svg';
            break;
        // field_qa_category_tag_document_request
        case 'field_qa_category_tag_document_request':
            iconPath = '/assets/image/icon-field0.svg';
            break;
        // field_qa_category_tag_document_others
        case 'field_qa_category_tag_document_others':
            iconPath = '/assets/image/icon-field0.svg';
            break;
        // field_qa_status
        case 'field_qa_status':
            iconPath = '/assets/image/icon-field0.svg';
            break;
        // field_qa_status_status_received
        case 'field_qa_status_status_received':
            iconPath = '/assets/image/icon-field4.svg';
            break;
        // field_qa_status_status_set_assignee
        case 'field_qa_status_status_set_assignee':
            iconPath = '/assets/image/icon-field4.svg';
            break;
        // field_qa_status_status_start_supporting
        case 'field_qa_status_status_start_supporting':
            iconPath = '/assets/image/icon-field4.svg';
            break;
        // field_qa_status_status_finished
        case 'field_qa_status_status_finished':
            iconPath = '/assets/image/icon-field4.svg';
            break;
        // field_qa_status_status_close
        case 'field_qa_status_status_close':
            iconPath = '/assets/image/icon-field4.svg';
            break;   
        // field status as new project created
        case 'field_status':
            iconPath = '/assets/image/icon-field4.svg';
            break;
        // field status waiting
        case 'field_status_waiting':
            iconPath = '/assets/image/icon-field4.svg';
            break;
        // field status todo
        case 'field_status_todo':
            iconPath = '/assets/image/icon-field4.svg';
            break;
        // field status doing
        case 'field_status_doing':
            iconPath = '/assets/image/icon-field4.svg';
            break;
        // field status done
        case 'field_status_done':
            iconPath = '/assets/image/icon-field4.svg';
            break;
        // field status pending
        case 'field_status_pending':
            iconPath = '/assets/image/icon-field4.svg';
            break;
        // customerEmail
        case 'customerEmail':
            iconPath = '/assets/image/icon-field02.svg';
            break;
        // customerLastName
        case 'customerLastName':
            iconPath = '/assets/image/icon-field02.svg';
            break;
        case 'proxiedUser':
            iconPath = '/assets/image/icon-field.svg';
            break;
        case 'personInCharge':
            iconPath = '/assets/image/icon-field.svg';
            break;
        // customerFirstName
        case 'customerFirstName':
            iconPath = '/assets/image/icon-field02.svg';
            break;

        case 'system_tags_form':
            iconPath = '/assets/image/icon-field.svg';
            break;  
        case 'creator':
            iconPath = '/assets/image/icon-field.svg';
            break;
        case 'updator':
            iconPath = '/assets/image/icon-field.svg';
            break;
        case 'suffixOfAttachments':
            iconPath = '/assets/image/icon-field.svg';
            break;
            case 'privateBookmark':
            iconPath = '/assets/image/icon-field.svg';
            break;
        case 'sharedBookmark':
            iconPath = '/assets/image/icon-field.svg';
            break;
        case 'ticket_type_category':
            iconPath = '/assets/image/icon-field.svg';
            break;
        case 'inquiry_source_project':
            iconPath = '/assets/image/icon-field.svg';
            break;
        case 'notice_target_project':
            iconPath = '/assets/image/icon-field.svg';
            break;
        case 'notice_target_user':
            iconPath = '/assets/image/icon-field.svg';
            break;
        case 'email_origin':
            iconPath = '/assets/image/icon-field.svg';
            break;
        case 'active_archived_status':
            iconPath = '/assets/image/icon-field.svg';
            break;
        case 'post_target_project':
            iconPath = '/assets/image/icon-field.svg';
            break;
        case Constant.SYSTEM_FIELD.REASONING_EFFORT:
        case Constant.SYSTEM_FIELD.BUSINESS_APP_USE_RANGE:
        case Constant.SYSTEM_FIELD.BUSINESS_APP_DISPLAY_SIDE_BAR:
        case Constant.SYSTEM_FIELD.MCP_SERVER_SETTING:
        case TelephoneConstant.TELEPHONE.SYSTEM_FIELD.CALL_STATUS:
        case TelephoneConstant.TELEPHONE.SYSTEM_FIELD.CALL_TYPE:
        case TelephoneConstant.TELEPHONE.SYSTEM_FIELD.CALL_HANDLER:
        case TelephoneConstant.TELEPHONE.SYSTEM_FIELD.CALL_MODE:
        case TelephoneConstant.TELEPHONE.SYSTEM_FIELD.CALLER_LANGUAGE:
        case TelephoneConstant.TELEPHONE.SYSTEM_FIELD.RECEIVER_LANGUAGE:
        case TelephoneConstant.TELEPHONE.SYSTEM_FIELD.REVIEW_STATUS:
        case TelephoneConstant.TELEPHONE.SYSTEM_FIELD.FOLLOW_UP_STATUS:
        case TelephoneConstant.TELEPHONE.SYSTEM_FIELD.EVALUATOR:
            iconPath = '/assets/image/icon-field.svg';
            break;
        // custom field
        // task
        case Constant.FORM_TYPE.FORM_TASK:
            iconPath = '/assets/image/icon-field0.svg';
            break;
        // knowledge
        case Constant.FORM_TYPE.FORM_KNOWLEDGE:
            iconPath = '/assets/image/icon-field04.svg';
            break;
        // text
        case Constant.FORM_TYPE.FIELD_TEXT:
        case TelephoneConstant.TELEPHONE.SYSTEM_FIELD.FROM:
        case TelephoneConstant.TELEPHONE.SYSTEM_FIELD.TO:
            iconPath = '/assets/image/icon-field02.svg';
            break;
        // keyword
        case Constant.FORM_TYPE.FIELD_KEYWORD:
            iconPath = '/assets/image/icon-field02.svg';
            break;
        // int
        case Constant.FORM_TYPE.FIELD_INT:
            iconPath = '/assets/image/icon-field03.svg';
            break;
        // fload
        case Constant.FORM_TYPE.FIELD_FLOAT:
            iconPath = '/assets/image/icon-field03.svg';
            break;
        // rich text
        case Constant.FORM_TYPE.FIELD_RICHTEXT:
        case Constant.AI_PROMPT.AI_PROMPT_BODY:
            iconPath = '/assets/image/icon-field07.svg';
            break;
        // url
        case Constant.FORM_TYPE.FIELD_URL:
            iconPath = '/assets/image/icon-field06.svg';
            break;
        // date
        case Constant.FORM_TYPE.FIELD_DATE:
            iconPath = '/assets/image/icon-field01.svg';
            break;
        // time
        case Constant.FORM_TYPE.FIELD_TIME:
            iconPath = '/assets/image/timer.png';
            break;
        // date time
        case Constant.FORM_TYPE.FIELD_DATE_TIME:
            iconPath = '/assets/image/icon-field01.svg';
            break;
        // tag
        case Constant.FORM_TYPE.FIELD_TAG:
        case Constant.AI_PROMPT.APPLY_AI_PROMPT:
        case Constant.AI_PROMPT.AI_PROMPT_APPLICATION_TARGET:
        case Constant.FORM_TYPE.FIELD_WORKFLOW:
        case Constant.SYSTEM_FIELD.KIBANA_ANALYSIS: // 分析機能 - CR-297430
            iconPath = '/assets/image/icon-field.svg';
            break;
        // iframe
        case Constant.FORM_TYPE.FIELD_IFRAME:
            iconPath = '/assets/image/icon-field05.svg';
            break;
        case Constant.FORM_TYPE.MERMAID:
            iconPath = '/assets/image/mermaid-icon.svg';
            break;
        case Constant.FORM_TYPE.HTML:
            iconPath = '/assets/image/html-icon.svg';
            break;
        case Constant.FORM_TYPE.MARKDOWN:
            iconPath = '/assets/image/markdown-field-icon.svg';
            break;
        case Constant.FORM_TYPE.JSON:
            iconPath = '/assets/image/json-field-icon.svg';
            break;
        case Constant.SYSTEM_FIELD.AI_TELEPHONE_REALTIME_MODEL:
        case Constant.SYSTEM_FIELD.AI_TELEPHONE_TRIGGER_CONDITION:
        case Constant.SYSTEM_FIELD.AI_TELEPHONE_RECEIVING_CALL_EXECUTION_MODE:
        case Constant.SYSTEM_FIELD.AI_TELEPHONE_EXECUTION_SCOPE:
        case Constant.SYSTEM_FIELD.AI_TELEPHONE_VOICE_SETTINGS:
            iconPath = '/assets/image/icon-field.svg';
            break;
        default:
            iconPath = '/assets/image/icon-field02.svg'
            break;
    }

    return iconPath;
};




///////////////////////////////////////////////////// hot fix refactor function //////////////////////////////////////////////////////
export const onChangeFieldNameREF = (event, currentFieldData, argsAll) => {
    let textValue = event.target.value.trim();
    let cloneFieldAddList = [...argsAll.fieldAddList];
    
    // validate text add error
    let validateResult = validateNameOfField(textValue);
    if (!Utils.isEmptyObject(validateResult)) {
        argsAll.setIsChangeData(false);
    }
    let errors = currentFieldData.errors ? currentFieldData.errors   : {};
    errors.name = validateResult;
    currentFieldData.errors = errors;

    // set text to current field name
    let newName = {
        jp: {
            A: 'F',
            text: textValue
        }
    };
    currentFieldData.name = newName;

    // set usingProjects
    currentFieldData.usingProjects = argsAll.isUsingProject ? [argsAll.projectDetail.id] : [];
    currentFieldData.isUsingProject = argsAll.isUsingProject;

    cloneFieldAddList.splice(argsAll.indexAddField, 1, { ...currentFieldData });
    
    if (argsAll.requiredFields) {
        // const isUnitFieldEnter = argsAll.checkUnitFieldEnter();
        if (textValue.length !== 0) {
            argsAll.setIsChangeData(true);
            argsAll.setFieldAddList(cloneFieldAddList);
        } else {
            argsAll.setIsChangeData(false);
            argsAll.setFieldAddList(cloneFieldAddList);
        }
    } else {
        if (textValue.length === 0) {
            argsAll.setIsChangeData(false);
            argsAll.setFieldAddList(cloneFieldAddList);
        } else if (textValue.length === 1) {
            argsAll.setIsChangeData(true);
            argsAll.setFieldAddList(cloneFieldAddList);
        }
    }

    updateDataOnChangeFieldName(argsAll, currentFieldData, newName, false);
    
    return currentFieldData;
}
 
export const onBlurUpdateField = (event, fieldData, argsAll) => {
    // Update in fieldAddList
    let cloneFieldAddList = [...argsAll.fieldAddList];
    let textValue = event.target.value.trim();
    // has field name and not
    if (fieldData?.name && Utils.isTypeObject(fieldData.name)) {
        let oldObjectName = Utils.convertJSONDataToObject(fieldData.name);
        const oldName = oldObjectName?.jp?.text;
        if (oldName && oldName === textValue) {
            return;
        }
    }
    
    let newName = {
        jp: {
            A: 'F',
            text: textValue
        }
    };

    fieldData.name = newName;
    fieldData.usingProjects = argsAll.isUsingProject ? [argsAll.projectDetail.id] : [];
    fieldData.isUsingProject = argsAll.isUsingProject;

    // let textValue = fieldData?.name?.jp?.text;
    cloneFieldAddList.splice(argsAll.indexAddField, 1, { ...fieldData });

    argsAll.setFieldAddList(cloneFieldAddList);

    if (argsAll.requiredFields) {
        // const isUnitFieldEnter = argsAll.checkUnitFieldEnter();
        if (textValue.length !== 0) {
            argsAll.setIsChangeData(true);
        } else {
            argsAll.setIsChangeData(false);
        }
    } else {
        if (textValue.length !== 0) {
            argsAll.setIsChangeData(true);
        } else {
            argsAll.setIsChangeData(false);
        }
    }

    updateDataOnChangeFieldName(argsAll, fieldData, newName);
};


export const isDisplayOptionShareField = (
    parentPanelScreen,
    projectInuseId,
    fieldAffiliationProject,
    rootFlag
) => {
    return (
        parentPanelScreen !== Constant.FORM_MANAGEMENT_SCREEN.ADD_FORM &&
        parentPanelScreen !== Constant.FORM_MANAGEMENT_SCREEN.EDIT_FORM &&
        projectInuseId === fieldAffiliationProject &&
        rootFlag === Constant.ROOT_FLAG.TRUE
    );
};

export const closePanelFormManagement = (closePanelArgs) => {
    const {
        panelList = [],
        setPanelList = () => {},
        currentIndex = 0,
        setCurrentIndex = () => {},
    } = closePanelArgs;
    
    if (Utils.isEmptyArray(panelList)) return;

    const formManagementPanelIndex = panelList.findIndex(panel => panel.type === Constant.PANEL_TYPE.FORMAT_MANAGEMENT);
    if (formManagementPanelIndex === -1) return;

    const filteredPanelList = panelList.filter((_, index) => index !== formManagementPanelIndex);
    setPanelList(filteredPanelList);

    let isCloseLastPanel = formManagementPanelIndex === filteredPanelList.length - 1;
    let newIndex = calculateIndexClosePanel(
        isCloseLastPanel,
        filteredPanelList,
        panelList[formManagementPanelIndex],
        formManagementPanelIndex,
        currentIndex
    );
    setCurrentIndex(newIndex);
};

/**
 * Handler: Save and proceed for AddNewFormDev
 * Validates field list and form name, then submits new form
 * @moved_from AddNewFormDev.js - okCallBack logic
 */
export const handleSaveAddNewForm = async ({
    fieldTypeSelectedList,
    fieldListInState,
    setFieldAddList,
    setFieldTypeSelectedList,
    trigger,
    isDisableButtonSubmit,
    props,
    handleSubmit,
    setFormPanelTabClose,
    onSubmitAddForm,
    argsAll,
    isSynonym,
    formPanelTabClose,
    panelList,
    setPanelList,
    currentIndex,
    setCurrentIndex
}) => {
    // validate add field list
    let isValidateError = false;
    if (!Utils.isEmptyArray(fieldTypeSelectedList)) {
        let validateAllAddFields = validateFieldList(
            fieldTypeSelectedList,
            false,
            fieldListInState
        );
        
        setFieldAddList(validateAllAddFields.fieldList);
        setFieldTypeSelectedList(validateAllAddFields.fieldList);
        isValidateError = validateAllAddFields.isValidateError;
    }

    // validate react-hook-form (formName)
    const isValidHookForm = await trigger();

    if (isDisableButtonSubmit() || !isValidHookForm || isValidateError) {
        props?.setIsOpenUnsavedChangesDialog(false);
        return;
    }
    
    handleSubmit((data) => {
        props?.setIsOpenUnsavedChangesDialog(false);
        setFormPanelTabClose({
            isDirty: false,
            isClosePanel: false,
        });
        onSubmitAddForm(data, argsAll, isSynonym)
            .then(() => {
                if (formPanelTabClose.isClosePanel) {
                    closePanelFormManagement({panelList, setPanelList, currentIndex, setCurrentIndex});
                    return;
                }
                props?.setTabSelected(Constant.TAB_FORM_MANAGEMENT.FIELD);
            })
            .catch(err => console.log('onSubmitAddForm err >>> :', err));
    })();
};

/**
 * Handler: Save and proceed for EditFormDev
 * Validates field list and form name, then submits edited form
 * @moved_from EditFormDev.js - okCallBack logic
 */
export const handleSaveEditForm = async ({
    fieldTypeSelectedList,
    fieldListInState,
    setFieldAddList,
    setFieldTypeSelectedList,
    trigger,
    isDisableButtonSubmit,
    props,
    handleSubmit,
    setFormPanelTabClose,
    onSubmitEditForm,
    formEdit,
    argsAll,
    isSynonym,
    formPanelTabClose,
    panelList,
    setPanelList,
    currentIndex,
    setCurrentIndex
}) => {
    // validate add field list
    let isValidateError = false;
    if (!Utils.isEmptyArray(fieldTypeSelectedList)) {
        let validateAllAddFields = validateFieldList(
            fieldTypeSelectedList,
            false,
            fieldListInState
        );
        
        setFieldAddList(validateAllAddFields.fieldList);
        setFieldTypeSelectedList(validateAllAddFields.fieldList);
        isValidateError = validateAllAddFields.isValidateError;
    }

    // validate react-hook-form (formName)
    const isValidHookForm = await trigger();

    if (isDisableButtonSubmit() || !isValidHookForm || isValidateError) {
        props?.setIsOpenUnsavedChangesDialog(false);
        return;
    }
    
    handleSubmit((data) => {
        props?.setIsOpenUnsavedChangesDialog(false);
        setFormPanelTabClose({
            isDirty: false,
            isClosePanel: false,
        });
        onSubmitEditForm(data.name, formEdit.type, argsAll, isSynonym)
            .then(() => {
                if (formPanelTabClose.isClosePanel) {
                    closePanelFormManagement({panelList, setPanelList, currentIndex, setCurrentIndex});
                    return;
                }
                props?.setTabSelected(Constant.TAB_FORM_MANAGEMENT.FIELD);
            })
            .catch(err => console.log('onSubmitAddForm err >>> :', err));
    })();
};

/**
 * Handler: Save and proceed for AddFieldTagItem
 * Validates tag field and items, then submits
 * @moved_from addField/AddFieldTagItem.js - okCallBack logic
 */
export const handleSaveAddFieldTagItem = ({
    selectedField,
    setSelectedField,
    itemList,
    setItemList,
    isDisableBtnSave,
    props,
    setFormPanelTabClose,
    onSubmitAddFieldTagItem,
    formPanelTabClose,
    panelList,
    setPanelList,
    currentIndex,
    setCurrentIndex
}) => {
    // validate error, close dialog without save
    if (Utils.isEmptyString(selectedField?.name)) {
        setSelectedField(prev => ({
            ...prev,
            error: trI18n('MSG-CM-REQUIRED-INPUT')
        }));
    }

    let isHasErrorItem = false;
    if (!Utils.isEmptyArray(itemList)) {
        let isHasError = false;
        const cloneItemList = [...itemList].map((item) => {
            const isAddNewItem = item.id.includes('item_new');
            if (isAddNewItem) return item;
            const itemName = item?.name?.trim() || '';
            isHasError = Utils.isEmptyString(itemName) || itemName.length > Constant.NAME_TAG_FIELD_MAX_LENGTH;
            if (!isHasError) return item;

            return {
                ...item,
                isErrorValidate: true,
                isEditName: true,
                errorMsg: Utils.isEmptyString(itemName)
                    ? trI18n('TAGMSG001')
                    : trI18n('MSG-CM-OVER-MAXLENGTH').replace(
                        '{{maxLength}}',
                        `${Constant.NAME_TAG_FIELD_MAX_LENGTH}`
                    )
            };
        });
        if (isHasError) {
            isHasErrorItem = true;
            setItemList(cloneItemList);
        }
    }

    if (isDisableBtnSave || Utils.isEmptyString(selectedField?.name) || isHasErrorItem) {
        props?.setIsOpenUnsavedChangesDialog(false);
        return;
    }

    props?.setIsOpenUnsavedChangesDialog(false);
    setFormPanelTabClose({
        isDirty: false,
        isClosePanel: false,
    });
    onSubmitAddFieldTagItem()
        .then(() => {
            if (formPanelTabClose.isClosePanel) {
                closePanelFormManagement({panelList, setPanelList, currentIndex, setCurrentIndex});
                return;
            }
            const preSelectedTab = props?.tabSelected;
            props?.setTabSelected(preSelectedTab === Constant.TAB_FORM_MANAGEMENT.FIELD
                ? Constant.TAB_FORM_MANAGEMENT.FORM
                : Constant.TAB_FORM_MANAGEMENT.FIELD
            );
        })
        .catch(err => console.log('onSubmitAddFieldTagItem err >>> :', err));
};

/**
 * Handler: Save and proceed for AddFieldStatusItem
 * Validates and submits status item list
 * @moved_from addField/AddFieldStatusItem.js - okCallBack logic
 */
export const handleSaveAddFieldStatusItem = ({
    isDisableBtnSave,
    props,
    setFormPanelTabClose,
    onSubmitSaveItemList,
    argsAll,
    formPanelTabClose,
    panelList,
    setPanelList,
    currentIndex,
    setCurrentIndex
}) => {
    if (isDisableBtnSave) {
        props?.setIsOpenUnsavedChangesDialog(false);
        return;
    }
    
    props?.setIsOpenUnsavedChangesDialog(false);
    setFormPanelTabClose({
        isDirty: false,
        isClosePanel: false,
    });
    onSubmitSaveItemList(argsAll, props)
        .then(() => {
            if (formPanelTabClose.isClosePanel) {
                closePanelFormManagement({panelList, setPanelList, currentIndex, setCurrentIndex});
                return;
            }
            const preSelectedTab = props?.tabSelected;
            props?.setTabSelected(preSelectedTab === Constant.TAB_FORM_MANAGEMENT.FIELD
                ? Constant.TAB_FORM_MANAGEMENT.FORM
                : Constant.TAB_FORM_MANAGEMENT.FIELD
            );
        })
        .catch(err => console.log('onSubmitSaveItemList err >>> :', err));
};

/**
 * Check if a tag item is a ChatGPT model tag
 * @param {Object} tagItem - Tag item object with sysField or originalSysField property
 * @returns {boolean} - True if tag item is ChatGPT model tag
 */
export const isChatGPTModelTagItem = (tagItem) => {
    const sysFieldToCheck = tagItem?.originalSysField || tagItem?.sysField;
    if (!sysFieldToCheck) return false;
    return sysFieldToCheck.startsWith(Constant.ID_PREFIX.CHATGPT_MODEL_TAG_PREFIX);
};



/**
 * Check if a tag item represents an unsupported ChatGPT model
 * @param {Object} tagItem - Tag item object
 * @param {Object} corporationSetting - Corporation setting object with listChatGptModel
 * @returns {boolean} - True if tag item is unsupported ChatGPT model
 */
export const isModelUnsupportedForTagItem = (tagItem, corporationSetting) => {
    if (!isChatGPTModelTagItem(tagItem)) {
        // Check unsupported realtime model by display name
        const tagItemName = Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, tagItem?.name) || '';
        if (tagItemName && TelephoneConstant.UNSUPPORTED_REALTIME_MODELS.some(model => tagItemName.startsWith(model))) {
            return true;
        }
        return false;
    }

    const sysFieldToCheck = tagItem?.originalSysField || tagItem?.sysField;
    const modelId = OpenAiUtil.getModelIdFromTagSysField(sysFieldToCheck);
    if (!modelId) {
        return false;
    }

    const listChatGptModel = corporationSetting?.listChatGptModel || [];
    const model = listChatGptModel.find(item => item?.id?.toLowerCase() === modelId?.toLowerCase());
    
    return model?.status === Constant.CHAT_GPT_MODEL_STATUS.UNSUPPORT;
};
